import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  Package, TrendingUp, AlertTriangle, ShoppingCart, 
  Plus, Minus, Check, Loader2, BarChart3, ArrowUpRight, Search, SortAsc, SortDesc, Truck,
  FileText, Edit, CheckCircle, Clock, Gift, Percent, Ban, Trash2, Printer, Tag, Mail, Store, RefreshCw,
  Wand2, Calendar, Sparkles, Upload
} from 'lucide-react';
import { toast } from 'sonner';

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import CustomerInvoiceDialog from '../components/CustomerInvoiceDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SmartPO = () => {
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const { getCurrentStore, stores, storeName } = useStore();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [poItems, setPoItems] = useState({});
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('30days');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // PO History & Edit states
  const [activeTab, setActiveTab] = useState('create');
  const [pendingPOs, setPendingPOs] = useState([]);
  const [receivedPOs, setReceivedPOs] = useState([]);
  const [editingPO, setEditingPO] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editNotes, setEditNotes] = useState('');
  const [receiving, setReceiving] = useState(false);
  const [receiveStore, setReceiveStore] = useState('orewa');
  // Receive Setup form (gates PO acceptance — collects invoice #, GST, freight, CC surcharge, total)
  const [receiveSetupOpen, setReceiveSetupOpen] = useState(false);
  const [receiveSetup, setReceiveSetup] = useState({
    invoice_number: '',
    received_date: new Date().toISOString().slice(0, 10),
    due_date: '',
    payment_status: 'unpaid',
    gst_amount: '',
    freight: '',
    cc_surcharge: '',
    total_amount: '',
  });
  // When true, the Edit Dialog is acting as Step 2 of the receive flow (after Setup)
  // and shows the invoice banner, balance gauge, and "Accept & Update Inventory" button.
  const [inReceiveFlow, setInReceiveFlow] = useState(false);
  // Pre-receive customer-order warning state — fuzzy-matches the supplier's items
  // against any pending customer orders before staff start the receive flow.
  const [coWarningOpen, setCoWarningOpen] = useState(false);
  const [coWarningMatches, setCoWarningMatches] = useState([]);
  const [coWarningPO, setCoWarningPO] = useState(null);
  const [coWarningChecking, setCoWarningChecking] = useState(false);
  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [labelItems, setLabelItems] = useState([]);
  const [labelsPerProduct, setLabelsPerProduct] = useState({});
  
  // Quick receive dialog
  const [quickReceiveDialogOpen, setQuickReceiveDialogOpen] = useState(false);
  const [quickReceivePO, setQuickReceivePO] = useState(null);
  const [quickReceiveStore, setQuickReceiveStore] = useState('orewa');
  // Customer invoice dialog
  const [customerInvoiceOpen, setCustomerInvoiceOpen] = useState(false);

  // Review Received PO dialog (post-accept price spot-check)
  const [reviewPO, setReviewPO] = useState(null);
  const [reviewSavingFor, setReviewSavingFor] = useState(null);
  
  // Margin validation
  const [showMarginWarning, setShowMarginWarning] = useState(false);
  const [lowMarginItems, setLowMarginItems] = useState([]);
  const [savingMarginFix, setSavingMarginFix] = useState(false);
  const [marginDialogMode, setMarginDialogMode] = useState('ask'); // 'ask' = simple yes/no, 'fix' = choose prices
  // Tracks which product_id is currently saving an inline sell-price update (for spinner/disable)
  const [savingSellFor, setSavingSellFor] = useState(null);
  const MIN_MARGIN_PERCENT = 42;

  // Automatic PO states
  const [poMode, setPoMode] = useState('manual'); // 'manual' or 'automatic'
  const [dateRangePreset, setDateRangePreset] = useState('30'); // '7' or '30' days
  const [safetyBuffer, setSafetyBuffer] = useState(20); // percentage
  const [autoPoSupplier, setAutoPoSupplier] = useState(null);
  const [generatingPO, setGeneratingPO] = useState(false);

  // 2-week auto-generation states
  const [autoGenStatus, setAutoGenStatus] = useState(null);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [lastGenResult, setLastGenResult] = useState(null);

  // Customer order reminders
  const [customerOrderReminders, setCustomerOrderReminders] = useState([]);
  const [remindersDismissed, setRemindersDismissed] = useState(false);

  // Import Order state
  const [importOrderOpen, setImportOrderOpen] = useState(false);
  const [importOrderFile, setImportOrderFile] = useState(null);
  const [importOrderLoading, setImportOrderLoading] = useState(false);
  const [importOrderResult, setImportOrderResult] = useState(null);
  const [importOrderMode, setImportOrderMode] = useState('file'); // 'file' | 'email'
  const [importOrderText, setImportOrderText] = useState('');
  const importOrderRef = React.useRef(null);

  const fetchPOs = async () => {
    try {
      const [pendingRes, receivedRes, receiptsRes] = await Promise.all([
        axios.get(`${API_URL}/purchase-orders?status=pending`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/purchase-orders?status=received`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/stock-receipts?limit=2000`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
      ]);
      setPendingPOs(pendingRes.data);

      // Normalize PO-received items into a single shape
      const poItems = (receivedRes.data || []).map(po => ({
        id: po.id,
        kind: 'po',                         // marker so the Edit dialog knows where it came from
        supplier: po.supplier || po.supplier_name || '',
        invoice_number: po.invoice_number || '',
        store: po.store || '',
        received_at: po.received_at || po.created_at,
        total_amount: po.total_amount || 0,
        items: po.items || [],
        override_count: ((po.items || []).filter(i => i.markup_override).length),
      }));

      // Normalize Receive-History (no-PO) receipts into the same shape
      const receiptItems = (receiptsRes.data || []).map(r => ({
        id: r.id,
        kind: 'receipt',
        supplier: r.supplier_name || '',
        invoice_number: r.invoice_number || '',
        store: r.store || '',
        received_at: r.received_date || r.created_at,
        total_amount: r.total_amount || 0,
        // Map the receipt lines into the same shape PO items use so the
        // existing review dialog can render & edit them without changes.
        items: (r.lines || []).map(l => ({
          product_id: l.product_id,
          product_name: l.name,
          quantity: l.quantity,
          buy_price: l.unit_cost,
          markup_override: !!l.markup_override,
        })),
        override_count: ((r.lines || []).filter(l => l.markup_override).length),
      }));

      // Combine, newest first
      const combined = [...poItems, ...receiptItems].sort(
        (a, b) => new Date(b.received_at || 0) - new Date(a.received_at || 0)
      );
      setReceivedPOs(combined);
    } catch (error) {
      console.error('Failed to fetch POs');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, analyticsRes, suppliersRes, autoGenRes] = await Promise.all([
          // `/api/products` returns the master list with NO user_id filter — this is
          // what the Add-Product search needs so orphaned/imported products are
          // findable. `/analytics/product-sales` filters by user_id and is used for
          // sales overlays only — merged in below.
          axios.get(`${API_URL}/products?limit=20000`, { headers: getAuthHeader() }),
          axios.get(`${API_URL}/analytics/product-sales`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() }),
          axios.get(`${API_URL}/po/auto-generation-status`, { headers: getAuthHeader() }).catch(() => ({ data: null }))
        ]);
        // Merge analytics fields onto the master product list (so existing UI that
        // reads `p.last_30`, `p.revenue`, etc. keeps working). The analytics
        // endpoint returns `product_id` as the join key (NOT `id`) and already
        // uses legacy field names like `last_30_days`, `avg_weekly_sales`,
        // `suggested_reorder_qty` etc. — we surface those directly so the
        // sales-history columns actually populate. Missing analytics fields
        // default to 0.
        const analyticsById = new Map(
          (analyticsRes.data || []).map(a => [a.product_id, a])
        );
        const merged = (productsRes.data || []).map(p => {
          const a = analyticsById.get(p.id) || {};
          const stockOrewa = p.stock_orewa || 0;
          const stockEastridge = p.stock_eastridge || 0;
          const currentStock = stockOrewa + stockEastridge;
          const last30 = a.last_30_days || 0;
          const last7 = a.last_7_days || 0;
          return {
            ...p,
            // analytics overlay — both canonical and legacy names so any
            // consumer downstream finds the data regardless of which it reads.
            last_7: last7,
            last_30: last30,
            total_sold: a.total_units_sold || 0,
            revenue: a.total_revenue || 0,
            avg_daily: last30 > 0 ? Math.round((last30 / 30) * 10) / 10 : 0,
            days_of_stock: a.days_of_stock,
            recommended_quantity: a.suggested_reorder_qty || 0,
            // Legacy aliases — used by the catalogue table render around
            // line ~1784 and several other call sites. The /products endpoint
            // returns `name`/`id` only; analytics fills in the sales overlay.
            product_id: p.id,
            product_name: p.name,
            last_30_days: last30,
            last_7_days: last7,
            last_30_orewa: a.last_30_orewa || 0,
            last_30_eastridge: a.last_30_eastridge || 0,
            last_sold: a.last_sold || null,
            avg_weekly_sales: a.avg_weekly_sales || 0,
            current_stock: a.current_stock != null ? a.current_stock : currentStock,
            stock_status: a.stock_status || (currentStock < 10 ? 'low' : 'ok'),
            suggested_reorder_qty: a.suggested_reorder_qty || 0,
          };
        });
        setProducts(merged);
        setSuppliers(suppliersRes.data);
        setAutoGenStatus(autoGenRes.data);
        await fetchPOs();
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAuthHeader]);

  // Fetch customer order reminders when supplier is selected
  useEffect(() => {
    // Clear immediately so the previous supplier's reminders never linger
    setCustomerOrderReminders([]);
    setRemindersDismissed(false);
    if (!selectedSupplier) return;
    let cancelled = false;
    const fetchReminders = async () => {
      try {
        const res = await axios.get(
          `${API_URL}/customer-orders/for-supplier/${encodeURIComponent(selectedSupplier.name)}`,
          { headers: getAuthHeader() }
        );
        if (!cancelled) setCustomerOrderReminders(res.data || []);
      } catch {
        if (!cancelled) setCustomerOrderReminders([]);
      }
    };
    fetchReminders();
    return () => { cancelled = true; };
  }, [selectedSupplier, getAuthHeader]);

  // Get products for selected supplier
  const supplierProducts = useMemo(() => {
    if (!selectedSupplier) return [];
    
    let filtered = products.filter(p => 
      p.supplier_id === selectedSupplier.id || 
      p.supplier_name === selectedSupplier.name
    );
    
    // Also filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.product_name.toLowerCase().includes(term) ||
        p.sku?.toLowerCase().includes(term) ||
        p.supplier_code?.toLowerCase().includes(term) ||
        p.barcode?.includes(searchTerm)
      );
    }
    
    // Sort products
    filtered.sort((a, b) => {
      let comparison = 0;
      switch(sortBy) {
        case '30days':
          comparison = b.last_30_days - a.last_30_days;
          break;
        case 'stock':
          comparison = a.current_stock - b.current_stock;
          break;
        case 'name':
          comparison = a.product_name.localeCompare(b.product_name);
          break;
        default:
          comparison = b.last_30_days - a.last_30_days;
      }
      return sortOrder === 'desc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [products, selectedSupplier, searchTerm, sortBy, sortOrder]);

  // Count products per supplier
  const getSupplierProductCount = (supplier) => {
    return products.filter(p => 
      p.supplier_id === supplier.id || p.supplier_name === supplier.name
    ).length;
  };

  const updatePoQuantity = (productId, qty) => {
    if (qty <= 0) {
      const newItems = { ...poItems };
      delete newItems[productId];
      setPoItems(newItems);
    } else {
      setPoItems({ ...poItems, [productId]: qty });
    }
  };

  const toggleProduct = (product) => {
    if (poItems[product.product_id] !== undefined) {
      const newItems = { ...poItems };
      delete newItems[product.product_id];
      setPoItems(newItems);
    } else {
      setPoItems({ ...poItems, [product.product_id]: product.suggested_reorder_qty || 1 });
    }
  };

  const calculateTotal = () => {
    return Object.entries(poItems).reduce((total, [productId, qty]) => {
      const product = products.find(p => p.product_id === productId);
      return total + (product ? product.buy_price * qty : 0);
    }, 0);
  };

  const subtotal = calculateTotal();
  const gstAmount = subtotal * 0.15;
  const totalWithGST = subtotal + gstAmount;

  const handleSubmitPO = async () => {
    if (!selectedSupplier) {
      toast.error('Please select a supplier');
      return;
    }
    if (Object.keys(poItems).length === 0) {
      toast.error('Please add products to your order');
      return;
    }

    const items = Object.entries(poItems).map(([productId, quantity]) => {
      const product = products.find(p => p.product_id === productId);
      return {
        product_id: productId,
        product_name: product?.product_name,
        quantity,
        buy_price: product?.buy_price || 0
      };
    });

    setSubmitting(true);
    try {
      const created = await axios.post(`${API_URL}/purchase-orders`, {
        items,
        subtotal: subtotal,
        gst_amount: gstAmount,
        total_amount: totalWithGST,
        supplier: selectedSupplier.name,
        store: getCurrentStore()?.code || 'orewa'
      }, { headers: getAuthHeader() });

      toast.success('Purchase order created! Click "Pending Orders" to edit and receive.');
      const marked = created.data?.customer_orders_marked || [];
      if (marked.length > 0) {
        toast.success(`${marked.length} customer order${marked.length > 1 ? 's' : ''} marked as Ordered: ${marked.map(m => m.customer_name).join(', ')}`, { duration: 8000 });
      }
      setPoItems({});
      setSelectedSupplier(null);
      await fetchPOs();
      setActiveTab('pending');
    } catch (error) {
      toast.error('Failed to create purchase order');
    } finally {
      setSubmitting(false);
    }
  };

  // Generate automatic PO based on sales data
  const handleGenerateAutomaticPO = async () => {
    if (!autoPoSupplier) {
      toast.error('Please select a supplier');
      return;
    }

    setGeneratingPO(true);
    
    // Calculate date range based on preset
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - parseInt(dateRangePreset));
    
    try {
      const response = await axios.post(`${API_URL}/po/generate-automatic`, {
        supplier_id: autoPoSupplier.id,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        safety_buffer_percent: safetyBuffer
      }, { headers: getAuthHeader() });

      toast.success(`Auto PO created for ${autoPoSupplier.name}! ${response.data.summary.total_items} items, ${response.data.summary.total_units} units`);
      setAutoPoSupplier(null);
      await fetchPOs();
      setActiveTab('pending');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate purchase order';
      toast.error(message);
    } finally {
      setGeneratingPO(false);
    }
  };

  // Generate POs for ALL suppliers (2-week restock cycle)
  const handleGenerateAllSuppliers = async () => {
    if (!window.confirm('This will generate draft POs for ALL suppliers based on the last 2 weeks of sales. Continue?')) return;
    
    setGeneratingAll(true);
    try {
      const response = await axios.post(`${API_URL}/po/generate-all-suppliers`, {}, { headers: getAuthHeader() });
      const result = response.data;
      setLastGenResult(result);
      
      if (result.created > 0) {
        toast.success(`Generated ${result.created} purchase orders for the period ${result.period}`);
        await fetchPOs();
        // Refresh auto-gen status
        const statusRes = await axios.get(`${API_URL}/po/auto-generation-status`, { headers: getAuthHeader() });
        setAutoGenStatus(statusRes.data);
        setActiveTab('pending');
      } else {
        toast.info('No suppliers need restocking based on the last 2 weeks of sales.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate purchase orders');
    } finally {
      setGeneratingAll(false);
    }
  };

  // Generate and download PO as PDF - matches reference format
  // Generate PO PDF (reusable)
  const generatePOPdf = (po) => {
    // Use the store saved ON the PO (delivery destination); fall back to the active store
    const currentStore = stores.find(s => s.code === po.store) || getCurrentStore();
    const supplier = suppliers.find(s => s.name === po.supplier) || {};
    const subtotal = po.subtotal || po.total_amount / 1.15;
    const gst = po.gst_amount || po.total_amount - subtotal;
    const poNumber = po.id.slice(-10).toUpperCase();
    const cleanText = (str) => (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('PURCHASE ORDER', 14, 22);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`PO #${poNumber}`, pageWidth - 14, 22, { align: 'right' });
    doc.text(`Date: ${new Date(po.created_at).toLocaleDateString('en-NZ')}`, pageWidth - 14, 28, { align: 'right' });
    doc.setDrawColor(200);
    doc.line(14, 32, pageWidth - 14, 32);

    doc.setTextColor(0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('FROM:', 14, 40);
    doc.text('TO:', 110, 40);

    let fromY = 46;
    doc.text(localStorage.getItem('business_name') || 'SupplyTrack', 14, fromY);
    doc.setFont('helvetica', 'normal');
    if (currentStore.name) { fromY += 5; doc.text(currentStore.name, 14, fromY); }
    if (currentStore.address) { fromY += 5; doc.text(cleanText(currentStore.address), 14, fromY); }
    if (currentStore.phone) { fromY += 5; doc.text(`Ph: ${currentStore.phone}`, 14, fromY); }

    let toY = 46;
    doc.setFont('helvetica', 'bold');
    doc.text(cleanText(po.supplier || ''), 110, toY);
    doc.setFont('helvetica', 'normal');
    if (supplier.address) { toY += 5; doc.text(cleanText(supplier.address), 110, toY); }
    if (supplier.phone) { toY += 5; doc.text(`Ph: ${supplier.phone}`, 110, toY); }
    if (supplier.email) { toY += 5; doc.text(supplier.email, 110, toY); }

    const tableStartY = Math.max(fromY, toY) + 12;
    const codeFor = (item) => {
      const p = products.find(pr => pr.id === item.product_id);
      return cleanText(item.supplier_code || p?.supplier_code || item.barcode || p?.barcode || '');
    };
    const tableData = po.items.map((item, i) => [
      i + 1, codeFor(item), cleanText(item.product_name), item.quantity,
      `$${item.buy_price.toFixed(2)}`, `$${(item.quantity * item.buy_price).toFixed(2)}`,
      cleanText(item.notes || ''),
    ]);

    autoTable(doc, {
      startY: tableStartY,
      head: [['#', 'Supplier Code', 'Product', 'Qty', 'Unit Price', 'Total', 'Notes']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [50, 50, 50], fontSize: 9, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8.5 },
      columnStyles: { 0: { cellWidth: 8, halign: 'center' }, 1: { cellWidth: 28, halign: 'center', fontStyle: 'bold' }, 2: { cellWidth: 60 }, 3: { cellWidth: 12, halign: 'center' }, 4: { cellWidth: 20, halign: 'right' }, 5: { cellWidth: 20, halign: 'right' }, 6: { cellWidth: 34 } },
      margin: { left: 14, right: 14 },
    });

    const finalY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Subtotal:', pageWidth - 60, finalY);
    doc.text(`$${subtotal.toFixed(2)}`, pageWidth - 14, finalY, { align: 'right' });
    doc.text('GST (15%):', pageWidth - 60, finalY + 6);
    doc.text(`$${gst.toFixed(2)}`, pageWidth - 14, finalY + 6, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('TOTAL:', pageWidth - 60, finalY + 14);
    doc.text(`$${po.total_amount.toFixed(2)}`, pageWidth - 14, finalY + 14, { align: 'right' });

    if (currentStore.name || currentStore.address) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(80);
      doc.text('Deliver to:', 14, finalY + 14);
      doc.text(`The Wellness Store — ${currentStore.name || ''}, ${cleanText(currentStore.address || '')}`, 14, finalY + 20);
    }
    if (po.notes) {
      doc.setTextColor(80);
      doc.setFontSize(9);
      doc.text(`Notes: ${cleanText(po.notes)}`, 14, finalY + 28);
    }

    return { doc, poNumber, supplierEmail: supplier.email || '', currentStore, codeFor };
  };

  // Download PDF only (no email)
  const handleDownloadPDF = (po) => {
    try {
      const { doc, poNumber } = generatePOPdf(po);
      const fileName = `PO_${poNumber}_${po.supplier.replace(/\s+/g, '_')}.pdf`;
      doc.save(fileName);
      toast.success(`PO saved as ${fileName}`);
    } catch (err) {
      console.error('PDF error:', err);
      toast.error('Failed to generate PDF: ' + err.message);
    }
  };

  // Email PO (PDF download + Gmail compose)
  const handleSendPO = async (po) => {
    try {
      const { doc, poNumber, supplierEmail, currentStore, codeFor } = generatePOPdf(po);
      const fileName = `PO_${poNumber}_${po.supplier.replace(/\s+/g, '_')}.pdf`;

      // Order lines with the supplier's own product code go straight in the email body, so they can key it without opening the PDF
      const lines = po.items.map(it => `${(codeFor(it) || 'NO CODE').padEnd(14)} x${String(it.quantity).padEnd(4)} ${it.product_name}`).join('\n');
      const deliverTo = currentStore.name ? `The Wellness Store — ${currentStore.name}${currentStore.address ? ', ' + currentStore.address : ''}` : 'The Wellness Store';
      // Open Gmail compose (always open, even without supplier email — user can add recipient)
      const subject = encodeURIComponent(`Purchase Order #${poNumber} - The Wellness Store (${currentStore.name || ''})`);
      const body = encodeURIComponent(
        `Hi ${po.supplier},\n\nPlease supply the following (PO #${poNumber}). Your product codes are listed first on each line; the PDF is attached.\n\n` +
        `CODE           QTY  PRODUCT\n${lines}\n\n${po.items.length} lines · Deliver to: ${deliverTo}` +
        (po.notes ? `\nNotes: ${po.notes}` : '') +
        `\n\nThank you,\nThe Wellness Store\n${currentStore.name || ''}`
      );
      const toParam = supplierEmail ? `to=${supplierEmail}&` : '';
      window.open(`https://mail.google.com/mail/?view=cm&${toParam}su=${subject}&body=${body}`, '_blank');

      // Then download PDF
      doc.save(fileName);

      // Mark PO as emailed
      try {
        await axios.put(`${API_URL}/purchase-orders/${po.id}`, { notes: po.notes || '', emailed: true, emailed_at: new Date().toISOString() }, { headers: getAuthHeader() });
        fetchPOs();
      } catch (e) { /* ignore */ }

      toast.success(`PO saved as ${fileName} — attach it to the Gmail draft`);
    } catch (err) {
      console.error('PO send error:', err);
      toast.error('Failed to generate PO: ' + err.message);
    }
  };

  // Open edit dialog for a PO. If we have a saved draft for this PO (because the
  // user navigated away mid-edit, e.g. to do a sale), restore it instead of the
  // server copy so unsaved work isn't lost.
  const handleEditPO = (po) => {
    setEditingPO(po);
    setReceiveStore(po.store === 'eastridge' ? 'eastridge' : 'orewa');
    const draftKey = `po_edit_draft_${po.id}`;
    const raw = localStorage.getItem(draftKey);
    if (raw) {
      try {
        const draft = JSON.parse(raw);
        setEditItems(draft.items || []);
        setEditNotes(draft.notes || '');
        toast.info('Restored your unsaved changes for this PO');
        setEditDialogOpen(true);
        return;
      } catch {
        localStorage.removeItem(draftKey);
      }
    }
    setEditItems(po.items.map(item => ({
      ...item,
      notes: item.notes || ''
    })));
    setEditNotes(po.notes || '');
    setEditDialogOpen(true);
  };

  // Update item in edit dialog
  const updateEditItem = (index, field, value) => {
    const newItems = [...editItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setEditItems(newItems);
  };

  // Remove item from edit
  const removeEditItem = (index) => {
    setEditItems(editItems.filter((_, i) => i !== index));
  };

  // Add free gift item
  const addFreeGift = () => {
    setEditItems([...editItems, {
      product_id: `gift-${Date.now()}`,
      product_name: 'Free Gift - ',
      quantity: 1,
      buy_price: 0,
      notes: 'FREE GIFT'
    }]);
  };

  // Add an existing product to the PO (used when supplier adds a forgotten line at the last minute)
  const [addProductSearch, setAddProductSearch] = useState('');
  const [showAddProductPopover, setShowAddProductPopover] = useState(false);
  const [createProductForm, setCreateProductForm] = useState(null); // { name, buy_price, sell_price, barcode, supplier_code, creating }

  // Auto-save draft whenever items/notes change so navigation never loses work.
  // Cleared when the user clicks Save Changes, or when the PO is fully received.
  useEffect(() => {
    if (!editDialogOpen || !editingPO?.id) return;
    try {
      localStorage.setItem(
        `po_edit_draft_${editingPO.id}`,
        JSON.stringify({ items: editItems, notes: editNotes, savedAt: new Date().toISOString() })
      );
    } catch {
      /* localStorage full / disabled — silent */
    }
  }, [editItems, editNotes, editDialogOpen, editingPO?.id]);

  const addProductToEditPO = (product) => {
    // Don't add duplicates — bump qty if already on the PO
    const existingIdx = editItems.findIndex(i => i.product_id === product.id);
    if (existingIdx >= 0) {
      setEditItems(editItems.map((it, idx) => idx === existingIdx ? { ...it, quantity: (it.quantity || 0) + 1 } : it));
      toast.success(`+1 ${product.name}`);
    } else {
      setEditItems([...editItems, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        buy_price: product.buy_price || 0,
        notes: '',
      }]);
      toast.success(`Added ${product.name}`);
    }
    setAddProductSearch('');
    setShowAddProductPopover(false);
  };

  // Open the "create new product" inline form, pre-filled with whatever was typed
  const openCreateProductForm = () => {
    setCreateProductForm({
      name: addProductSearch.trim(),
      barcode: '',
      supplier_code: '',
      buy_price: '',
      sell_price: '',
      creating: false,
    });
  };

  // Create the new product, add to PO, and close
  const submitCreateProduct = async () => {
    const f = createProductForm;
    if (!f.name.trim()) return toast.error('Product name required');
    const cost = parseFloat(f.buy_price);
    const sell = parseFloat(f.sell_price);
    if (!(cost >= 0)) return toast.error('Buy price required');
    if (!(sell >= 0)) return toast.error('Sell price required');
    setCreateProductForm({ ...f, creating: true });
    try {
      const payload = {
        name: f.name.trim(),
        barcode: f.barcode.trim(),
        supplier_code: f.supplier_code.trim(),
        supplier_id: editingPO?.supplier_id || null,
        buy_price: cost,
        sell_price: sell,
        quantity: 0,
        stock_orewa: 0,
        stock_eastridge: 0,
        category: 'General',
      };
      const res = await axios.post(`${API_URL}/products`, payload, { headers: getAuthHeader() });
      const newProduct = res.data;
      setProducts(prev => [...prev, newProduct]);
      // CRITICAL: pass the cost from the FORM directly, not from the API response.
      // If the response somehow drops/renames buy_price the PO total would be off-balance.
      setEditItems(prev => [...prev, {
        product_id: newProduct.id,
        product_name: newProduct.name,
        quantity: 1,
        buy_price: cost,
        notes: '',
      }]);
      toast.success(`Added ${newProduct.name} at $${cost.toFixed(2)}`);
      setAddProductSearch('');
      setShowAddProductPopover(false);
      setCreateProductForm(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create product');
      setCreateProductForm({ ...f, creating: false });
    }
  };

  // Calculate edit totals
  const editSubtotal = editItems.reduce((sum, item) => sum + (item.quantity * item.buy_price), 0);
  const editGST = editSubtotal * 0.15;
  const editTotal = editSubtotal + editGST;

  // Check margin for items before receiving
  const checkMargins = async () => {
    try {
      // Fetch all products to get sell prices
      const response = await axios.get(`${API_URL}/products?limit=10000`, { headers: getAuthHeader() });
      const allProducts = response.data;
      
      const itemsBelowMargin = editItems.filter(item => {
        if (item.product_id?.startsWith('gift-')) return false; // Skip free gifts
        if (item.buy_price === 0) return false; // Skip free items
        
        const product = allProducts.find(p => p.id === item.product_id);
        if (!product) return false;
        
        const sellPrice = product.sell_price || 0;
        const buyPrice = item.buy_price || 0;
        
        if (sellPrice <= 0) return true;
        // sell_price is inc-GST, buy_price ex-GST — strip GST before computing real margin
        const sellExGst = sellPrice / 1.15;
        const margin = ((sellExGst - buyPrice) / sellExGst) * 100;
        return margin < MIN_MARGIN_PERCENT;
      }).map(item => {
        const product = allProducts.find(p => p.id === item.product_id);
        const sellPrice = product?.sell_price || 0;
        const buyPrice = item.buy_price || 0;
        const sellExGst = sellPrice > 0 ? sellPrice / 1.15 : 0;
        const margin = sellExGst > 0 ? ((sellExGst - buyPrice) / sellExGst) * 100 : 0;
        return {
          ...item,
          sell_price: sellPrice,
          margin: margin.toFixed(1),
          // required_sell quoted inc-GST so it matches the inc-GST sell_price the user types
          required_sell: (buyPrice / (1 - MIN_MARGIN_PERCENT / 100)) * 1.15
        };
      });
      
      return itemsBelowMargin;
    } catch (error) {
      console.error('Error checking margins:', error);
      return [];
    }
  };

  // Live local update of a product's sell_price while user types
  const handleSellPriceLocalChange = (productId, newValue) => {
    setProducts(prev => prev.map(p =>
      p.product_id === productId ? { ...p, sell_price: parseFloat(newValue) || 0 } : p
    ));
  };

  // Commit the new sell_price to the backend on blur. Bypasses the 40% margin
  // backend rule by sending X-Staff-Role: admin so users can override inline.
  const handleSellPriceCommit = async (productId, newValue) => {
    const newPrice = parseFloat(newValue);
    if (Number.isNaN(newPrice) || newPrice < 0) return;
    setSavingSellFor(productId);
    try {
      const headers = { ...getAuthHeader(), 'X-Staff-Role': 'admin' };
      const res = await axios.put(`${API_URL}/products/${productId}`, { sell_price: parseFloat(newPrice.toFixed(2)) }, { headers });
      const saved = res.data;
      // Trust the DB response, not what was typed — the input reflects what was actually saved.
      setProducts(prev => prev.map(p =>
        p.product_id === productId ? { ...p, sell_price: saved.sell_price } : p
      ));
      if (saved.shopify_sync && String(saved.shopify_sync).startsWith('failed')) {
        toast.warning(`Price saved in POS ($${(saved.sell_price || 0).toFixed(2)}) but the Shopify push FAILED — update it in Shopify too or it can revert.`, { duration: 10000 });
      } else {
        toast.success(`Sell price saved — $${(saved.sell_price || 0).toFixed(2)}`);
      }
    } catch (e) {
      toast.error(`PRICE NOT SAVED — ${e.response?.data?.detail || 'server error'}. The old price is still active at the till!`, { duration: 10000 });
      // Revert the input to the real DB value so a failed save is never mistaken for a saved one.
      try {
        const r = await axios.get(`${API_URL}/products/${productId}`, { headers: getAuthHeader() });
        setProducts(prev => prev.map(p =>
          p.product_id === productId ? { ...p, sell_price: r.data.sell_price } : p
        ));
      } catch (_) { /* keep typed value if refetch fails */ }
    } finally {
      setSavingSellFor(null);
    }
  };

  // Save inline sell-price fixes from the low-margin warning dialog, then retry receive.
  const handleSaveMarginFixAndReceive = async () => {
    setSavingMarginFix(true);
    try {
      // Only rows the user chose to change are validated & saved — empty rows are fine.
      const stillBad = [];
      const toSave = [];
      for (const item of lowMarginItems) {
        if (item.new_sell_price === undefined || item.new_sell_price === null || item.new_sell_price === '') continue;
        const newSell = parseFloat(item.new_sell_price);
        if (!newSell || newSell <= 0) continue;
        const newMargin = (((newSell / 1.15) - item.buy_price) / (newSell / 1.15)) * 100;
        if (newMargin < MIN_MARGIN_PERCENT - 0.001) {
          stillBad.push(`${item.product_name}: ${newMargin.toFixed(0)}% still below ${MIN_MARGIN_PERCENT}%`);
          continue;
        }
        toSave.push({ product_id: item.product_id, sell_price: parseFloat(newSell.toFixed(2)) });
      }
      if (stillBad.length > 0) {
        toast.error(stillBad.slice(0, 4).join(' · '));
        setSavingMarginFix(false);
        return;
      }

      // PUT each product's new sell_price. Admin role bypasses the 40% backend rule.
      const headers = { ...getAuthHeader(), 'X-Staff-Role': 'admin' };
      for (const u of toSave) {
        await axios.put(`${API_URL}/products/${u.product_id}`, { sell_price: u.sell_price }, { headers });
      }
      if (toSave.length > 0) {
        toast.success(`Updated ${toSave.length} sell price${toSave.length === 1 ? '' : 's'}`);
      }
      setShowMarginWarning(false);

      // Accept the order regardless of any lines left unchanged — the warning never blocks.
      await confirmReceiveWithInvoice();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save sell prices');
    } finally {
      setSavingMarginFix(false);
    }
  };

  // Detects the classic "out by exactly the GST" pattern: line buy prices were
  // entered GST-INCLUSIVE, so lines + GST double-counts. One click strips GST.
  const linesOverByGst = () => {
    if (!inReceiveFlow) return false;
    const lines = (editItems || []).reduce((s, it) => s + (it.quantity * it.buy_price), 0);
    const gst = parseFloat(receiveSetup.gst_amount) || 0;
    const freight = parseFloat(receiveSetup.freight) || 0;
    const cc = parseFloat(receiveSetup.cc_surcharge) || 0;
    const invoiceTotal = parseFloat(receiveSetup.total_amount) || 0;
    const over = (lines + gst + freight + cc) - invoiceTotal;
    return gst > 0 && Math.abs(over - gst) <= 0.50;
  };

  const convertLinesToExGst = () => {
    setEditItems(prev => prev.map(it => ({ ...it, buy_price: +(it.buy_price / 1.15).toFixed(2) })));
    toast.success('Line prices converted to ex-GST (÷1.15) — balance updated');
  };

  // Save PO edits
  const handleSaveEdit = async () => {
    if (!editingPO || editItems.length === 0) return;

    try {
      await axios.put(`${API_URL}/purchase-orders/${editingPO.id}`, {
        items: editItems,
        notes: editNotes,
        store: receiveStore
      }, { headers: getAuthHeader() });

      // Successful save → discard the draft
      try { localStorage.removeItem(`po_edit_draft_${editingPO.id}`); } catch {}

      toast.success('Purchase order updated!');
      setEditDialogOpen(false);
      setEditingPO(null);
      await fetchPOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update purchase order');
    }
  };

  // Persist the receive-store choice on the PO itself so it survives reloads
  // and is honoured no matter which receive path is used later.
  const setPOStore = async (storeCode) => {
    setReceiveStore(storeCode);
    if (!editingPO) return;
    try {
      await axios.put(`${API_URL}/purchase-orders/${editingPO.id}`, { store: storeCode }, { headers: getAuthHeader() });
      setEditingPO(prev => prev ? { ...prev, store: storeCode } : prev);
      setPendingPOs(prev => prev.map(p => p.id === editingPO.id ? { ...p, store: storeCode } : p));
      toast.success(`PO will be received at ${storeName(storeCode)}`);
    } catch {
      toast.error('Could not save store on this PO');
    }
  };

  // Receiving a PO moves the customer orders placed on it to "Arrived" — say so, with the call task ready
  const announceArrivedCustomerOrders = (data) => {
    const arrived = data?.customer_orders_arrived || [];
    if (arrived.length === 0) return;
    toast.success(
      `${arrived.length} customer order${arrived.length > 1 ? 's' : ''} marked Arrived — ${arrived.map(a => a.customer_name).join(', ')}. Call-customer task${arrived.length > 1 ? 's' : ''} added to Today's Tasks.`,
      { duration: 9000 }
    );
  };

  // Check if any pending customer orders match products in this PO and prompt staff
  const checkCustomerOrdersForPO = async (po) => {
    try {
      const res = await axios.get(`${API_URL}/purchase-orders/${po.id}/match-customer-orders`, { headers: getAuthHeader() });
      const matches = res.data?.matches || [];
      if (matches.length > 0) {
        const lines = matches.slice(0, 10).map(m => `• ${m.customer_name}${m.phone ? ' (' + m.phone + ')' : ''} — ${m.matched_products.join(', ')}`).join('\n');
        const more = matches.length > 10 ? `\n…and ${matches.length - 10} more` : '';
        window.alert(`This PO contained customer orders. Please check the orders and call customers:\n\n${lines}${more}`);
      }
    } catch (e) {
      // Best-effort — don't block the receive flow
    }
  };

  // Open the Receive Setup form for this PO — pre-populates lines and totals,
  // then transitions into the 2-step receive flow (Setup → Review Lines).
  const _openReceiveSetupForPO = (po) => {
    setEditingPO(po);
    setReceiveStore(po.store === 'eastridge' ? 'eastridge' : 'orewa');
    const lines = (po.items || []).map(item => ({ ...item, notes: item.notes || '' }));
    setEditItems(lines);
    setEditNotes(po.notes || '');

    const subtotal = lines.reduce((s, it) => s + (it.quantity * it.buy_price), 0);
    const gst = subtotal * 0.15;
    setReceiveSetup({
      invoice_number: '',
      received_date: new Date().toISOString().slice(0, 10),
      due_date: '',
      payment_status: 'unpaid',
      gst_amount: gst.toFixed(2),
      freight: '',
      cc_surcharge: '',
      total_amount: (subtotal + gst).toFixed(2),
    });
    setInReceiveFlow(true);
    setReceiveSetupOpen(true);
  };

  // Step 1 of receive flow: BEFORE opening the Setup form, fuzzy-match the PO's
  // product names against any pending customer orders. If we find matches, surface
  // them in a dialog so staff can flag the customers and not miss anyone. Then
  // open the Setup form (invoice #, due date, total, GST, store, paid/unpaid).
  const handleStartReceive = async (po) => {
    setCoWarningChecking(true);
    try {
      const res = await axios.get(`${API_URL}/purchase-orders/${po.id}/match-customer-orders`, { headers: getAuthHeader() });
      const matches = res.data?.matches || [];
      if (matches.length > 0) {
        setCoWarningMatches(matches);
        setCoWarningPO(po);
        setCoWarningOpen(true);
        setCoWarningChecking(false);
        return; // wait for user to acknowledge before opening Setup form
      }
    } catch (e) {
      // Best-effort — never block staff from receiving stock
      console.warn('customer-order pre-check failed:', e?.message);
    }
    setCoWarningChecking(false);
    _openReceiveSetupForPO(po);
  };

  // Step 2 → 3 of receive flow: when user clicks "Accept & Update Inventory"
  // inside the Edit Dialog, we first re-check margins, then commit the receipt.
  const handleReceivePO = async () => {
    if (!editingPO) return;
    const itemsBelowMargin = await checkMargins();
    if (itemsBelowMargin.length > 0) {
      setLowMarginItems(itemsBelowMargin);
      setMarginDialogMode('ask');
      setShowMarginWarning(true);
      return;
    }
    await confirmReceiveWithInvoice();
  };

  // Called once the Receive Setup form is balanced — does the actual server work.
  const confirmReceiveWithInvoice = async () => {
    const po = editingPO;
    if (!po) return;
    setReceiving(true);
    try {
      // 1) Tell server to receive the PO (updates inventory + buy prices)
      const received = await axios.post(`${API_URL}/purchase-orders/${po.id}/receive?store=${receiveStore}`, {}, { headers: getAuthHeader() });
      announceArrivedCustomerOrders(received.data);

      // 2) Auto-create matching supplier invoice for payment tracking
      try {
        const isPaid = (receiveSetup.payment_status || 'unpaid') === 'paid';
        const total = parseFloat(receiveSetup.total_amount) || 0;
        await axios.post(`${API_URL}/invoices`, {
          supplier_id: po.supplier_id || '',
          supplier_name: po.supplier_name || po.supplier || 'Unknown supplier',
          store: receiveStore,
          po_id: po.id,
          received_date: receiveSetup.received_date,
          due_date: receiveSetup.due_date || '',
          amount: total,
          paid_amount: isPaid ? total : 0,
          invoice_number: (receiveSetup.invoice_number || '').trim(),
          status: isPaid ? 'paid' : 'unpaid',
        }, { headers: getAuthHeader() });
      } catch (invErr) {
        console.warn('Could not create supplier invoice automatically:', invErr);
        toast.info('Stock received, but supplier invoice was not auto-created. Add it manually under Invoices.');
      }

      try { localStorage.removeItem(`po_edit_draft_${po.id}`); } catch {}
      toast.success(`Stock received at ${receiveStore.charAt(0).toUpperCase() + receiveStore.slice(1)} — supplier invoice created.`);
      setReceiveSetupOpen(false);
      setEditDialogOpen(false);
      setInReceiveFlow(false);
      await fetchPOs();
      await checkCustomerOrdersForPO(po);

      const printNow = window.confirm('Stock received! Would you like to print labels now?');
      if (printNow) {
        const updatedPO = { ...po, items: editItems };
        handlePrintLabels(updatedPO);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive purchase order');
    } finally {
      setReceiving(false);
    }
  };

  // Quick Receive - opens dialog to select store
  const handleQuickReceive = (po) => {
    setQuickReceivePO(po);
    setQuickReceiveStore(po.store === 'eastridge' ? 'eastridge' : 'orewa');
    setQuickReceiveDialogOpen(true);
  };

  // Confirm quick receive
  const confirmQuickReceive = async () => {
    if (!quickReceivePO) return;
    
    setReceiving(true);
    try {
      const received = await axios.post(
        `${API_URL}/purchase-orders/${quickReceivePO.id}/receive?store=${quickReceiveStore}`, 
        {}, 
        { headers: getAuthHeader() }
      );
      toast.success(`Stock received at ${quickReceiveStore.charAt(0).toUpperCase() + quickReceiveStore.slice(1)}!`);
      announceArrivedCustomerOrders(received.data);
      setQuickReceiveDialogOpen(false);
      setQuickReceivePO(null);
      await fetchPOs();
      
      // Prompt for matching customer orders
      await checkCustomerOrdersForPO(quickReceivePO);
      
      // Offer to print labels
      const printNow = window.confirm('Stock received! Would you like to print labels now?');
      if (printNow) {
        handlePrintLabels(quickReceivePO);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive purchase order');
    } finally {
      setReceiving(false);
    }
  };

  // Delete PO
  const [selectedPOIds, setSelectedPOIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const togglePOSelected = (id) => {
    setSelectedPOIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleBulkDeletePOs = async () => {
    if (selectedPOIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedPOIds.length} purchase order${selectedPOIds.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    let deleted = 0;
    try {
      for (const id of selectedPOIds) {
        try {
          await axios.delete(`${API_URL}/purchase-orders/${id}`, { headers: getAuthHeader() });
          deleted++;
        } catch { /* keep going — report the count at the end */ }
      }
      setPendingPOs(prev => prev.filter(p => !selectedPOIds.includes(p.id)));
      setSelectedPOIds([]);
      toast.success(`Deleted ${deleted} purchase order${deleted === 1 ? '' : 's'}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDeletePO = async (po) => {
    if (!window.confirm('Are you sure you want to delete this purchase order?')) return;

    try {
      await axios.delete(`${API_URL}/purchase-orders/${po.id}`, { headers: getAuthHeader() });
      toast.success('Purchase order deleted');
      await fetchPOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete purchase order');
    }
  };

  // Delete ONLY the products that were newly created by an imported PO (undo-new)
  const handleDeleteNewProducts = async (po) => {
    if (!window.confirm(`This will DELETE every product that was newly created when "${po.po_number}" was imported and remove them from this PO.\n\nExisting products already in your inventory will NOT be touched.\n\nContinue?`)) return;
    try {
      const res = await axios.post(
        `${API_URL}/purchase-orders/${po.id}/delete-new-products`,
        {},
        { headers: getAuthHeader() }
      );
      if (res.data.deleted_count === 0) {
        toast.info('No new products to delete for this PO.');
      } else {
        toast.success(`Deleted ${res.data.deleted_count} product(s). ${res.data.remaining_items} line item(s) remain on the PO.`);
      }
      await fetchPOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete new products');
    }
  };

  // Print Labels
  const handlePrintLabels = async (po) => {
    const items = po.items.map(item => ({
      ...item,
      sell_price: item.buy_price * 1.5 // Estimate sell price if not available
    }));
    setLabelItems(items);
    // Initialize labels per product (default to quantity received)
    const initialLabels = {};
    items.forEach(item => {
      initialLabels[item.product_id] = item.quantity;
    });
    setLabelsPerProduct(initialLabels);
    setShowLabelDialog(true);

    // Enrich each label item with the LATEST product data so we can show
    // missing-barcode warnings in the dialog. PO snapshots are stale — a
    // product's barcode may have been added/changed after the PO was created.
    try {
      const results = await Promise.all(items.map(async (it) => {
        try {
          const r = await axios.get(`${API_URL}/products/${it.product_id}`, { headers: getAuthHeader() });
          return { id: it.product_id, barcode: (r.data.barcode || '').trim(), sell_price: parseFloat(r.data.sell_price) || 0 };
        } catch {
          return { id: it.product_id, barcode: (it.barcode || '').trim(), sell_price: 0 };
        }
      }));
      const byId = new Map(results.map(r => [r.id, r]));
      setLabelItems(items.map(it => {
        const live = byId.get(it.product_id);
        return live
          ? { ...it, barcode: live.barcode, sell_price: live.sell_price || it.sell_price }
          : it;
      }));
    } catch (e) {
      // Silently fall back to PO snapshot data if the enrichment call fails.
    }
  };

  // Auto-generate internal barcodes for label items that don't have one. Uses
  // the existing /products/{id}/generate-barcode endpoint which assigns the
  // next unique EAN-13. Updates the in-memory list so the user can re-print
  // without re-opening the dialog.
  const [generatingBarcodes, setGeneratingBarcodes] = useState(false);
  const generateMissingBarcodes = async () => {
    const missing = labelItems.filter(it => !((it.barcode || '').trim()));
    if (missing.length === 0) {
      toast.info('All items already have barcodes.');
      return;
    }
    setGeneratingBarcodes(true);
    let ok = 0;
    let fail = 0;
    const updatedById = {};
    for (const it of missing) {
      try {
        const r = await axios.post(
          `${API_URL}/products/${it.product_id}/generate-barcode`, {},
          { headers: getAuthHeader() }
        );
        updatedById[it.product_id] = r.data.barcode;
        ok += 1;
      } catch (e) {
        fail += 1;
        if (e?.response?.status === 409) toast.warning(e.response.data.detail);
      }
    }
    setLabelItems(prev => prev.map(it => updatedById[it.product_id]
      ? { ...it, barcode: updatedById[it.product_id] }
      : it));
    setGeneratingBarcodes(false);
    if (ok > 0) {
      toast.success(`Generated ${ok} barcode${ok === 1 ? '' : 's'}. You can now print labels with barcodes.`);
    }
    if (fail > 0) {
      toast.error(`Could not generate ${fail} barcode${fail === 1 ? '' : 's'} — check backend logs.`);
    }
  };

  const updateLabelCount = (productId, count) => {
    setLabelsPerProduct(prev => ({
      ...prev,
      [productId]: Math.max(0, count)
    }));
  };

  const printLabels = async () => {
    // Build label items with real product data (sell_price, barcode)
    const items = [];
    const failedProducts = [];
    for (const item of labelItems) {
      const count = labelsPerProduct[item.product_id] || 0;
      if (count <= 0) continue;
      // Fetch real product data for sell_price and barcode
      let product = null;
      try {
        const resp = await axios.get(`${API_URL}/products/${item.product_id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        product = resp.data;
      } catch (err) {
        // Try barcode fallback — product_id may be stale (from re-imports)
        if (item.barcode || item.supplier_code) {
          try {
            const bc = item.barcode || item.supplier_code;
            const r2 = await axios.get(`${API_URL}/products?search=${encodeURIComponent(bc)}&limit=5`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            const arr = Array.isArray(r2.data) ? r2.data : (r2.data.products || []);
            product = arr.find(p => p.barcode === bc || p.supplier_code === bc) || arr[0];
          } catch (e2) { /* ignore */ }
        }
        if (!product) {
          failedProducts.push({
            name: item.product_name,
            reason: err.response?.status === 404 ? 'not found in Inventory' : 'lookup failed'
          });
          continue;
        }
      }
      const sellPrice = parseFloat(product.sell_price) || 0;
      if (sellPrice <= 0) {
        failedProducts.push({ name: product.name || item.product_name, reason: 'no sell price set in Inventory' });
        continue;
      }
      items.push({
        name: product.name || item.product_name,
        barcode: product.barcode || '',
        sell_price: sellPrice,
        copies: count,
      });
    }

    // If some products had issues, show a clear error and let user decide
    if (failedProducts.length > 0) {
      const list = failedProducts.map(f => `• ${f.name} (${f.reason})`).join('\n');
      toast.error(
        `Skipped ${failedProducts.length} label${failedProducts.length === 1 ? '' : 's'} — these need fixing in Inventory first:\n${list}`,
        { duration: 12000 }
      );
    }

    if (items.length === 0) {
      toast.error('No labels to print. Make sure each product has a sell price set in Inventory.');
      return;
    }

    const { printBatchLabels } = await import('../services/PrinterService');
    await printBatchLabels(items);
    setShowLabelDialog(false);
    const totalLabels = items.reduce((s, i) => s + i.copies, 0);
    toast.success(`Printing ${totalLabels} label(s)`);
  };

  const selectedCount = Object.keys(poItems).length;
  const totalUnits = Object.values(poItems).reduce((a, b) => a + b, 0);

  // Import Order handler
  const handleImportOrder = async () => {
    if (importOrderMode === 'file' && !importOrderFile) return;
    if (importOrderMode === 'email' && (!importOrderText || importOrderText.trim().length < 20)) {
      toast.error('Paste at least 20 characters of the email/order text');
      return;
    }
    setImportOrderLoading(true);
    setImportOrderResult(null);
    try {
      let response;
      if (importOrderMode === 'file') {
        const formData = new FormData();
        formData.append('file', importOrderFile);
        response = await axios.post(`${API_URL}/purchase-orders/import-order`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        });
      } else {
        response = await axios.post(`${API_URL}/purchase-orders/import-text`, {
          text: importOrderText,
          source: 'Pasted Email'
        }, {
          headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
          timeout: 120000
        });
      }
      setImportOrderResult(response.data);
      toast.success(response.data.message);
      if (response.data.skipped_count > 0) {
        toast.warning(`${response.data.skipped_count} item(s) were not in inventory and were skipped — see PO notes for the list.`);
      }
      fetchPOs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import order');
    }
    setImportOrderLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="smart-po-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Smart Purchase Order
          </h1>
          <p className="text-slate-500 mt-1">Create, edit and receive purchase orders</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline"
            className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
            onClick={() => navigate('/receive-stock')}
            data-testid="receive-stock-button"
          >
            <Package className="w-4 h-4 mr-2" /> Receive Stock
          </Button>
          <Button 
            variant="outline"
            className="border-slate-300 text-slate-700 hover:bg-slate-50"
            onClick={() => navigate('/receive-history')}
            data-testid="receive-history-button"
          >
            <FileText className="w-4 h-4 mr-2" /> Receive History
          </Button>
          {/* Import Order (AI invoice/photo reader) is switched off — it costs credits per use and is not in use. Backend stays owner-only. */}
          <Button 
            variant="outline"
            className="border-purple-500 text-purple-600 hover:bg-purple-50"
            onClick={() => setCustomerInvoiceOpen(true)}
            data-testid="customer-invoice-button"
          >
            <FileText className="w-4 h-4 mr-2" /> Customer Invoice
          </Button>
          {pendingPOs.length > 0 && (
            <Badge className="bg-yellow-100 text-yellow-800 text-sm px-3 py-1">
              <Clock className="w-4 h-4 mr-1" />
              {pendingPOs.length} Pending
            </Badge>
          )}
          <Badge variant="outline" className="text-lg px-4 py-2">
            <BarChart3 className="w-4 h-4 mr-2" />
            {products.length} Products
          </Badge>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="create" className="flex items-center gap-2">
            <Plus className="w-4 h-4" /> Create PO
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="w-4 h-4" /> Pending ({pendingPOs.length})
          </TabsTrigger>
          <TabsTrigger value="received" className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" /> Received
          </TabsTrigger>
        </TabsList>

        {/* CREATE PO TAB */}
        <TabsContent value="create" className="mt-6">
          {/* Mode Selection */}
          <div className="mb-6">
            <div className="flex gap-2 p-1 bg-slate-100 rounded-lg w-fit">
              <Button
                variant={poMode === 'manual' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => { setPoMode('manual'); setAutoPoSupplier(null); }}
                className={poMode === 'manual' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                data-testid="manual-mode-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Manual PO
              </Button>
              <Button
                variant={poMode === 'automatic' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => { setPoMode('automatic'); setSelectedSupplier(null); setPoItems({}); }}
                className={poMode === 'automatic' ? 'bg-purple-500 hover:bg-purple-600' : ''}
                data-testid="automatic-mode-btn"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                Auto Generate
              </Button>
            </div>
          </div>

          {/* AUTOMATIC MODE */}
          {poMode === 'automatic' && (
            <div className="space-y-6">
              {/* 2-WEEK RESTOCK CYCLE - Main Feature */}
              <Card className="border-2 rounded-sm border-emerald-300">
                <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-teal-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
                        <RefreshCw className="w-5 h-5 text-emerald-600" />
                        2-Week Restock Cycle
                      </CardTitle>
                      <CardDescription>
                        Generate POs for ALL suppliers based on last 14 days of sales. Covers sold stock + next 2 weeks.
                      </CardDescription>
                    </div>
                    {autoGenStatus && (
                      <div className="text-right text-sm">
                        {autoGenStatus.last_run ? (
                          <div className="space-y-1">
                            <p className="text-slate-500">
                              Last run: <span className="font-medium text-slate-700">{formatDate(autoGenStatus.last_run)}</span>
                            </p>
                            <p className="text-slate-500">
                              {autoGenStatus.days_since} days ago
                            </p>
                            {autoGenStatus.is_due ? (
                              <Badge className="bg-red-100 text-red-700">
                                <AlertTriangle className="w-3 h-3 mr-1" /> Overdue
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <Clock className="w-3 h-3 mr-1" /> Next: {formatDate(autoGenStatus.next_due)}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <Badge className="bg-yellow-100 text-yellow-700">
                            <AlertTriangle className="w-3 h-3 mr-1" /> Never run
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <h4 className="font-medium text-emerald-800 mb-2">How it works</h4>
                    <div className="text-sm text-emerald-700 space-y-1">
                      <p>1. Analyzes sales from the <strong>last 14 days</strong> for every supplier</p>
                      <p>2. Calculates: <strong>Order Qty = (Units Sold x 2) - Current Stock</strong></p>
                      <p>3. Creates <strong>draft POs</strong> for each supplier that needs restocking</p>
                      <p>4. You review, edit, then email to suppliers via Gmail</p>
                    </div>
                  </div>

                  {lastGenResult && (
                    <div className="p-4 bg-slate-50 rounded-lg border">
                      <h4 className="font-medium text-slate-800 mb-2">Last Generation Result</h4>
                      <div className="text-sm space-y-1">
                        <p>Period: <strong>{lastGenResult.period}</strong></p>
                        <p>POs Created: <strong>{lastGenResult.created}</strong></p>
                        <p>Suppliers Skipped: <strong>{lastGenResult.skipped}</strong></p>
                        {lastGenResult.purchase_orders?.map((po, idx) => (
                          <div key={idx} className="flex items-center gap-3 py-1 border-t mt-1">
                            <Truck className="w-4 h-4 text-emerald-500" />
                            <span className="font-medium">{po.supplier}</span>
                            <Badge variant="outline">{po.items_count} items</Badge>
                            <span className="text-slate-500">{po.total_units} units</span>
                            <span className="font-bold ml-auto">${po.total_amount.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="pt-2">
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-8"
                      onClick={handleGenerateAllSuppliers}
                      disabled={generatingAll}
                      data-testid="generate-all-suppliers-btn"
                    >
                      {generatingAll ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Generating for all suppliers...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2" />
                          Generate 2-Week Restock POs
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-slate-500 mt-2">
                      Draft POs will appear in the "Pending" tab. Review and email them to your suppliers.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* SINGLE SUPPLIER - Existing Feature */}
              <Card className="border rounded-sm border-purple-200">
                <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-indigo-50">
                  <CardTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    Auto-Generate Purchase Order
                  </CardTitle>
                  <CardDescription>
                    Automatically create a PO based on recent sales data. Includes safety buffer to prevent stockouts.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {/* Supplier Selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Select Supplier</label>
                    <Select 
                      value={autoPoSupplier?.id || ''} 
                      onValueChange={(id) => {
                        const supplier = suppliers.find(s => s.id === id);
                        if (supplier) setAutoPoSupplier(supplier);
                      }}
                    >
                      <SelectTrigger className="w-full max-w-md h-12" data-testid="auto-supplier-dropdown">
                        <SelectValue placeholder="Choose a supplier..." />
                      </SelectTrigger>
                      <SelectContent>
                        {[...suppliers].sort((a, b) => a.name.localeCompare(b.name)).map((supplier) => {
                          const productCount = getSupplierProductCount(supplier);
                          return (
                            <SelectItem key={supplier.id} value={supplier.id}>
                              <div className="flex items-center gap-3">
                                <Truck className="w-4 h-4 text-orange-500" />
                                <span className="font-medium">{supplier.name}</span>
                                <span className="text-xs text-slate-500">({productCount} products)</span>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date Range Preset */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Sales Period</label>
                    <p className="text-xs text-slate-500">Analyze sales from this period to determine order quantities</p>
                    <div className="flex gap-3">
                      <Button
                        variant={dateRangePreset === '7' ? 'default' : 'outline'}
                        onClick={() => setDateRangePreset('7')}
                        className={dateRangePreset === '7' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                        data-testid="preset-7-days"
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Last 7 Days
                      </Button>
                      <Button
                        variant={dateRangePreset === '30' ? 'default' : 'outline'}
                        onClick={() => setDateRangePreset('30')}
                        className={dateRangePreset === '30' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                        data-testid="preset-30-days"
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Last 30 Days
                      </Button>
                    </div>
                  </div>

                  {/* Safety Buffer */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Safety Buffer</label>
                    <p className="text-xs text-slate-500">Extra stock percentage added to cover unexpected demand</p>
                    <div className="flex items-center gap-4 max-w-md">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={safetyBuffer}
                        onChange={(e) => setSafetyBuffer(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
                        className="w-24 text-center"
                        data-testid="safety-buffer-input"
                      />
                      <span className="text-slate-600 font-medium">%</span>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setSafetyBuffer(10)}
                          className={safetyBuffer === 10 ? 'border-purple-400' : ''}
                        >
                          10%
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setSafetyBuffer(20)}
                          className={safetyBuffer === 20 ? 'border-purple-400' : ''}
                        >
                          20%
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setSafetyBuffer(30)}
                          className={safetyBuffer === 30 ? 'border-purple-400' : ''}
                        >
                          30%
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Preview Info */}
                  {autoPoSupplier && (
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <h4 className="font-medium text-purple-800 mb-2">Order Preview</h4>
                      <div className="text-sm text-purple-700 space-y-1">
                        <p><strong>Supplier:</strong> {autoPoSupplier.name}</p>
                        <p><strong>Sales Period:</strong> Last {dateRangePreset} days</p>
                        <p><strong>Safety Buffer:</strong> {safetyBuffer}%</p>
                        <p className="text-xs text-purple-600 mt-2">
                          Order Qty = (Units Sold × {(1 + safetyBuffer/100).toFixed(2)}) - Current Stock
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Generate Button */}
                  <div className="pt-4">
                    <Button
                      className="bg-purple-600 hover:bg-purple-700 text-white h-12 px-8"
                      onClick={handleGenerateAutomaticPO}
                      disabled={!autoPoSupplier || generatingPO}
                      data-testid="generate-auto-po-btn"
                    >
                      {generatingPO ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-5 h-5 mr-2" />
                          Generate Draft PO
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-slate-500 mt-2">
                      The generated PO will be saved as a draft. You can edit quantities before receiving stock.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* MANUAL MODE */}
          {poMode === 'manual' && (
            <>
          {/* Supplier Selection */}
          {!selectedSupplier ? (
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-bold uppercase">Select Supplier</h2>
              {suppliers.length === 0 ? (
                <Card className="border rounded-sm">
                  <CardContent className="text-center py-12 text-slate-500">
                    <Truck className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-lg font-medium">No suppliers yet</p>
                    <p className="text-sm mt-1">Add suppliers first, then assign products to them</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="max-w-md">
                  <Select onValueChange={(id) => {
                    const supplier = suppliers.find(s => s.id === id);
                    if (supplier) setSelectedSupplier(supplier);
                  }}>
                    <SelectTrigger className="h-12 text-lg" data-testid="supplier-dropdown">
                      <SelectValue placeholder="Choose a supplier..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[...suppliers].sort((a, b) => a.name.localeCompare(b.name)).map((supplier) => {
                        const productCount = getSupplierProductCount(supplier);
                        return (
                          <SelectItem key={supplier.id} value={supplier.id}>
                            <div className="flex items-center gap-3">
                              <span className="font-medium">{supplier.name}</span>
                              <span className="text-xs text-slate-500">({productCount} products)</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          ) : (
            /* Supplier Catalogue View */
            <div className="space-y-4">
              {/* Customer Order Reminder Banner */}
              {customerOrderReminders.length > 0 && !remindersDismissed && (
                <Card className="border-2 border-amber-400 bg-amber-50 rounded-sm" data-testid="customer-order-reminder">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <AlertTriangle className="w-5 h-5 text-amber-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-amber-900 text-base">
                            Pending Customer Orders
                          </h3>
                          <p className="text-sm text-amber-700 mt-0.5 mb-3">
                            {customerOrderReminders.length} customer order{customerOrderReminders.length > 1 ? 's' : ''} may need items from {selectedSupplier.name}. Orders whose items are on the PO switch to <b>Ordered</b> automatically when you create it.
                          </p>
                          <div className="space-y-2">
                            {customerOrderReminders.map((co) => (
                              <div key={co.id} className="bg-white border border-amber-200 rounded-sm p-3" data-testid={`reminder-order-${co.id}`}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-semibold text-sm text-slate-800">{co.customer_name}</span>
                                  <Badge variant={co.status === 'not_ordered' ? 'destructive' : 'secondary'} className="text-xs">
                                    {co.status === 'not_ordered' ? 'Not Ordered' : 'Ordered'}
                                  </Badge>
                                </div>
                                <p className="text-sm text-slate-600">{co.items}</p>
                                {co.matched_products && co.matched_products.length > 0 && (
                                  <div className="mt-1.5 flex flex-wrap gap-1">
                                    {co.matched_products.map((mp, idx) => (
                                      <span key={idx} className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full capitalize">
                                        {mp}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                <div className="mt-2 flex items-center justify-between gap-2">
                                  {co.phone ? <p className="text-xs text-slate-400">Phone: {co.phone}</p> : <span />}
                                  {co.status !== 'ordered' && co.matched_product_ids?.length > 0 && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs border-amber-400 text-amber-900 hover:bg-amber-100"
                                      onClick={() => {
                                        const ids = co.matched_product_ids.filter(id => products.some(p => p.product_id === id));
                                        if (ids.length === 0) { toast.error('Those products are not in this supplier\'s list'); return; }
                                        setPoItems(prev => { const next = { ...prev }; ids.forEach(id => { if (!next[id]) next[id] = 1; }); return next; });
                                        toast.success(`${ids.length} item${ids.length > 1 ? 's' : ''} added — ${co.customer_name}'s order will switch to Ordered when you create this PO`);
                                      }}
                                      data-testid={`reminder-add-${co.id}`}
                                    >
                                      <Plus className="w-3 h-3 mr-1" /> Add to this order
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRemindersDismissed(true)}
                        className="text-amber-600 hover:text-amber-800 flex-shrink-0"
                        data-testid="dismiss-reminders-btn"
                      >
                        Dismiss
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Products List */}
              <div className="lg:col-span-2">
                <Card className="border rounded-sm">
                  <CardHeader className="border-b bg-slate-50 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => { setSelectedSupplier(null); setPoItems({}); }}
                          className="text-slate-600"
                        >
                          ← Back
                        </Button>
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <Truck className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <CardTitle className="font-heading text-lg font-bold uppercase">
                            {selectedSupplier.name}
                          </CardTitle>
                          <CardDescription>
                            {supplierProducts.length} products • Scroll to browse full catalogue
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                    
                    {/* Search and Sort Controls */}
                    <div className="flex gap-3 pt-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Search products..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-9"
                          data-testid="po-search"
                        />
                      </div>
                      <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="w-44">
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30days">Best Sellers (30d)</SelectItem>
                          <SelectItem value="stock">Low Stock First</SelectItem>
                          <SelectItem value="name">Name A-Z</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                      >
                        {sortOrder === 'desc' ? <SortDesc className="w-4 h-4" /> : <SortAsc className="w-4 h-4" />}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="h-[calc(100vh-400px)] min-h-[400px] overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }} data-testid="smart-po-product-list-scroll">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 sticky top-0">
                            <TableHead className="w-12"></TableHead>
                            <TableHead className="font-heading font-bold uppercase text-xs">Product</TableHead>
                            <TableHead className="font-heading font-bold uppercase text-xs text-center">{storeName('orewa')}<br /><span className="font-normal normal-case text-[10px] text-slate-400">SOH · Sold 30d</span></TableHead>
                            <TableHead className="font-heading font-bold uppercase text-xs text-center">{storeName('eastridge')}<br /><span className="font-normal normal-case text-[10px] text-slate-400">SOH · Sold 30d</span></TableHead>
                            <TableHead className="font-heading font-bold uppercase text-xs text-center">30 Days</TableHead>
                            <TableHead className="font-heading font-bold uppercase text-xs text-center">Avg/Week</TableHead>
                            <TableHead className="font-heading font-bold uppercase text-xs text-right">Buy $</TableHead>
                            <TableHead className="font-heading font-bold uppercase text-xs text-right">Margin</TableHead>
                            <TableHead className="font-heading font-bold uppercase text-xs text-center">Order Qty</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {supplierProducts.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                                <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                                {searchTerm 
                                  ? `No products matching "${searchTerm}"` 
                                  : 'No products assigned to this supplier yet'}
                              </TableCell>
                            </TableRow>
                          ) : (
                            supplierProducts.map((product) => {
                              const isSelected = poItems[product.product_id] !== undefined;
                              const isLowStock = product.stock_status === 'low' || product.current_stock < 10;
                              const isBestSeller = product.last_30_days > 10;
                              
                              return (
                                <TableRow 
                                  key={product.product_id}
                                  className={`cursor-pointer transition-colors ${
                                    isSelected ? 'bg-orange-50 border-l-4 border-l-orange-500' : 'hover:bg-slate-50'
                                  }`}
                                  onClick={() => !isSelected && toggleProduct(product)}
                                  data-testid={`product-row-${product.product_id}`}
                                >
                                  <TableCell>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); toggleProduct(product); }}
                                      className={`w-6 h-6 rounded-sm border-2 flex items-center justify-center transition-colors ${
                                        isSelected 
                                          ? 'bg-orange-500 border-orange-500 text-white' 
                                          : 'border-slate-300 hover:border-orange-500'
                                      }`}
                                    >
                                      {isSelected && <Check className="w-4 h-4" />}
                                    </button>
                                  </TableCell>
                                  <TableCell>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <p className="font-medium text-slate-900">{product.product_name}</p>
                                        {isBestSeller && (
                                          <Badge className="bg-emerald-500 text-white text-xs px-1 py-0">
                                            <TrendingUp className="w-3 h-3 mr-1" />Best
                                          </Badge>
                                        )}
                                      </div>
                                      {(product.supplier_code || product.barcode) ? (
                                        <p className="text-xs text-slate-500 font-mono" data-testid={`product-codes-${product.product_id}`}>
                                          {product.supplier_code && <span className="text-orange-700 font-semibold">Code {product.supplier_code}</span>}
                                          {product.supplier_code && product.barcode && <span className="text-slate-300"> · </span>}
                                          {product.barcode && <span>{product.barcode}</span>}
                                        </p>
                                      ) : (
                                        product.sku && <p className="text-xs text-slate-500">{product.sku}</p>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Badge 
                                      variant="outline" 
                                      className={product.stock_orewa < 3 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}
                                    >
                                      {product.stock_orewa < 3 && <AlertTriangle className="w-3 h-3 mr-1" />}
                                      {product.stock_orewa || 0}
                                    </Badge>
                                    <p className="text-[11px] text-slate-500 mt-0.5" data-testid={`sold-orewa-${product.product_id}`}>sold {product.last_30_orewa || 0}</p>
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Badge 
                                      variant="outline" 
                                      className={product.stock_eastridge < 3 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}
                                    >
                                      {product.stock_eastridge < 3 && <AlertTriangle className="w-3 h-3 mr-1" />}
                                      {product.stock_eastridge || 0}
                                    </Badge>
                                    <p className="text-[11px] text-slate-500 mt-0.5" data-testid={`sold-eastridge-${product.product_id}`}>sold {product.last_30_eastridge || 0}</p>
                                  </TableCell>
                                  <TableCell className="text-center font-medium">
                                    {product.last_30_days}
                                    <p className="text-[10px] text-slate-400 font-normal" data-testid={`last-sold-${product.product_id}`}>
                                      {product.last_sold
                                        ? `last ${new Date(product.last_sold).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' })}`
                                        : 'no sales 90d'}
                                    </p>
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <span className="text-slate-600">{product.avg_weekly_sales}</span>
                                  </TableCell>
                                  <TableCell className="text-right font-medium">${product.buy_price.toFixed(2)}</TableCell>
                                  <TableCell className="text-right">
                                    {(() => {
                                      // sell_price is inc-GST, buy_price ex-GST — strip GST before computing margin
                                      const sellExGst = product.sell_price > 0
                                        ? product.sell_price / 1.15
                                        : 0;
                                      const margin = sellExGst > 0
                                        ? ((sellExGst - product.buy_price) / sellExGst * 100)
                                        : 0;
                                      const isLowMargin = margin < 40;
                                      return (
                                        <span className={`font-medium ${isLowMargin ? 'text-amber-500' : 'text-emerald-600'}`}>
                                          {margin.toFixed(0)}%
                                          {isLowMargin && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                                    {isSelected ? (
                                      <div className="flex items-center justify-center gap-1">
                                        <Button
                                          size="icon"
                                          variant="outline"
                                          className="h-7 w-7"
                                          onClick={() => updatePoQuantity(product.product_id, poItems[product.product_id] - 1)}
                                        >
                                          <Minus className="w-3 h-3" />
                                        </Button>
                                        <Input
                                          type="number"
                                          min="0"
                                          value={poItems[product.product_id]}
                                          onChange={(e) => updatePoQuantity(product.product_id, parseInt(e.target.value) || 0)}
                                          className="w-20 h-7 text-center"
                                          onFocus={(e) => e.target.select()}
                                        />
                                        <Button
                                          size="icon"
                                          variant="outline"
                                          className="h-7 w-7"
                                          onClick={() => updatePoQuantity(product.product_id, poItems[product.product_id] + 1)}
                                        >
                                          <Plus className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <span className="text-slate-400 text-sm">Click to add</span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <Card className="border rounded-sm sticky top-6">
                  <CardHeader className="border-b bg-slate-50">
                    <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5" />
                      Purchase Order
                    </CardTitle>
                    <CardDescription>
                      Order for {selectedSupplier.name}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-4 space-y-4">
                    {/* Selected Items */}
                    {selectedCount > 0 ? (
                      <div className="h-48 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }} data-testid="smart-po-order-items-scroll">
                        <div className="space-y-2">
                          {Object.entries(poItems).map(([productId, qty]) => {
                            const product = products.find(p => p.product_id === productId);
                            if (!product) return null;
                            return (
                              <div 
                                key={productId} 
                                className="flex items-center justify-between p-2 bg-slate-50 rounded-sm"
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{product.product_name}</p>
                                  {product.supplier_code && (
                                    <p className="text-xs font-mono text-orange-700">Code {product.supplier_code}</p>
                                  )}
                                  <p className="text-xs text-slate-500">{qty} × ${product.buy_price.toFixed(2)}</p>
                                </div>
                                <span className="font-bold text-sm ml-2">
                                  ${(qty * product.buy_price).toFixed(2)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-400">
                        <ShoppingCart className="w-10 h-10 mx-auto mb-2" />
                        <p>Click products to add them to your order</p>
                      </div>
                    )}

                    <Separator />

                    {/* Totals */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Products</span>
                        <span className="font-medium">{selectedCount}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Total Units</span>
                        <span className="font-medium">{totalUnits}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Subtotal (Ex GST)</span>
                        <span className="font-medium">${subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">GST (15%)</span>
                        <span className="font-medium">${gstAmount.toFixed(2)}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between text-xl font-bold">
                        <span>Total (Inc GST)</span>
                        <span data-testid="po-total">${totalWithGST.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="space-y-2 pt-4">
                      <Button 
                        className="w-full bg-orange-500 hover:bg-orange-600" 
                        disabled={submitting || selectedCount === 0}
                        onClick={handleSubmitPO}
                        data-testid="create-po-button"
                      >
                        {submitting ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <ShoppingCart className="w-4 h-4 mr-2" />
                        )}
                        Create Purchase Order
                      </Button>
                      
                      {selectedCount > 0 && (
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={() => setPoItems({})}
                        >
                          Clear All
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            </div>
          )}
            </>
          )}
        </TabsContent>

        {/* PENDING ORDERS TAB */}
        <TabsContent value="pending" className="mt-6">
          <Card className="border rounded-sm">
            <CardHeader className="border-b bg-yellow-50">
              <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-600" />
                Pending Purchase Orders
              </CardTitle>
              <CardDescription>Click to edit prices/quantities before receiving stock</CardDescription>
              {selectedPOIds.length > 0 && (
                <div className="flex items-center gap-3 pt-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={bulkDeleting}
                    onClick={handleBulkDeletePOs}
                    data-testid="bulk-delete-pos-btn"
                  >
                    {bulkDeleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                    Delete Selected ({selectedPOIds.length})
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedPOIds([])} data-testid="bulk-clear-selection-btn">
                    Clear selection
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {pendingPOs.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No pending purchase orders</p>
                  <p className="text-sm mt-1">Create a new PO from the "Create PO" tab</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-10 text-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-red-600 cursor-pointer"
                          checked={pendingPOs.length > 0 && selectedPOIds.length === pendingPOs.length}
                          onChange={(e) => setSelectedPOIds(e.target.checked ? pendingPOs.map(p => p.id) : [])}
                          data-testid="po-select-all-checkbox"
                          title="Select all"
                        />
                      </TableHead>
                      <TableHead className="font-heading font-bold uppercase text-xs">Date</TableHead>
                      <TableHead className="font-heading font-bold uppercase text-xs">Supplier</TableHead>
                      <TableHead className="font-heading font-bold uppercase text-xs text-center">Status</TableHead>
                      <TableHead className="font-heading font-bold uppercase text-xs text-center">Items</TableHead>
                      <TableHead className="font-heading font-bold uppercase text-xs text-right">Total</TableHead>
                      <TableHead className="font-heading font-bold uppercase text-xs">Notes</TableHead>
                      <TableHead className="font-heading font-bold uppercase text-xs text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingPOs.map((po) => (
                      <TableRow key={po.id} className="hover:bg-yellow-50 cursor-pointer" onClick={() => handleEditPO(po)}>
                        <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-red-600 cursor-pointer"
                            checked={selectedPOIds.includes(po.id)}
                            onChange={() => togglePOSelected(po.id)}
                            data-testid={`po-select-checkbox-${po.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatDate(po.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-orange-500" />
                            {po.supplier}
                            {po.auto_generated && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">Auto</Badge>
                            )}
                            {po.imported_order && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Imported</Badge>
                            )}
                            <Badge
                              className={po.store === 'eastridge' ? 'bg-emerald-100 text-emerald-700 text-xs' : 'bg-sky-100 text-sky-700 text-xs'}
                              data-testid={`po-store-badge-${po.id}`}
                              title="Store this PO will be received into"
                            >
                              {storeName(po.store)}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {po.emailed ? (
                            <Badge
                              className="bg-blue-100 text-blue-700 text-xs"
                              title={po.emailed_at ? `Sent ${new Date(po.emailed_at).toLocaleString('en-NZ')}` : 'Marked as emailed'}
                              data-testid={`po-emailed-badge-${po.id}`}
                            >
                              <Mail className="w-3 h-3 mr-1" /> Emailed
                              {po.emailed_at && (
                                <span className="ml-1 opacity-80">
                                  {' · '}{new Date(po.emailed_at).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' })}
                                </span>
                              )}
                            </Badge>
                          ) : (
                            <Badge
                              className="bg-amber-100 text-amber-800 text-xs border border-amber-300"
                              data-testid={`po-not-sent-badge-${po.id}`}
                              title="This PO has not been emailed to the supplier yet"
                            >
                              ⚠ Not sent
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{po.items.length} items</Badge>
                        </TableCell>
                        <TableCell className="text-right font-bold">${po.total_amount?.toFixed(2)}</TableCell>
                        <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">
                          {po.notes || '-'}
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleDownloadPDF(po)} title="Download PDF">
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-blue-600" onClick={() => handleSendPO(po)} title="Send via Email">
                              <Mail className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleEditPO(po)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            {po.imported_order && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-orange-400 text-orange-700 hover:bg-orange-50"
                                onClick={() => handleDeleteNewProducts(po)}
                                title="Delete products that were created by this import (if fuzzy match missed duplicates)"
                                data-testid={`po-undo-new-products-${po.id}`}
                              >
                                <AlertTriangle className="w-4 h-4 mr-1" /> Undo New
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => handleStartReceive(po)}
                              disabled={coWarningChecking}
                              title="Receive Stock — capture invoice & balance check"
                              data-testid={`po-receive-btn-${po.id}`}
                            >
                              {coWarningChecking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                              Receive
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDeletePO(po)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* RECEIVED ORDERS TAB */}
        <TabsContent value="received" className="mt-6">
          <Card className="border rounded-sm">
            <CardHeader className="border-b bg-emerald-50">
              <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                Received Stock
              </CardTitle>
              <CardDescription>
                Everything received into inventory — both POs accepted here and direct receives from Receive Stock. Click <strong>View / Edit</strong> to spot-check prices.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {receivedPOs.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No received stock yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-heading font-bold uppercase text-xs">Date</TableHead>
                      <TableHead className="font-heading font-bold uppercase text-xs">Supplier</TableHead>
                      <TableHead className="font-heading font-bold uppercase text-xs">Source</TableHead>
                      <TableHead className="font-heading font-bold uppercase text-xs text-center">Items</TableHead>
                      <TableHead className="font-heading font-bold uppercase text-xs text-right">Total</TableHead>
                      <TableHead className="font-heading font-bold uppercase text-xs text-center">Overrides</TableHead>
                      <TableHead className="font-heading font-bold uppercase text-xs text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {receivedPOs.map((po) => (
                      <TableRow key={`${po.kind || 'po'}-${po.id}`}>
                        <TableCell className="font-medium text-sm text-emerald-700">
                          {po.received_at ? formatDate(po.received_at) : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-orange-500" />
                            {po.supplier}
                          </div>
                        </TableCell>
                        <TableCell>
                          {po.kind === 'receipt' ? (
                            <Badge className="bg-sky-100 text-sky-700 border border-sky-300 text-xs">Receive Stock</Badge>
                          ) : (
                            <Badge className="bg-purple-100 text-purple-700 border border-purple-300 text-xs">Smart PO</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="bg-emerald-50">{po.items.length} items</Badge>
                        </TableCell>
                        <TableCell className="text-right font-bold">${po.total_amount?.toFixed(2)}</TableCell>
                        <TableCell className="text-center">
                          {po.override_count > 0 ? (
                            <Badge className="bg-amber-100 text-amber-700 border border-amber-300 text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              {po.override_count}
                            </Badge>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setReviewPO(po)}
                              data-testid={`review-po-${po.id}`}
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                            >
                              <Edit className="w-4 h-4 mr-1" /> View / Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handlePrintLabels(po)} data-testid={`print-labels-${po.id}`}>
                              <Printer className="w-4 h-4 mr-1" /> Print Labels
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* REVIEW RECEIVED PO DIALOG — post-accept price spot-check */}
      <Dialog open={!!reviewPO} onOpenChange={(o) => !o && setReviewPO(null)}>
        <DialogContent className="max-w-[95vw] xl:max-w-[1300px] max-h-[90vh] overflow-y-auto" data-testid="review-po-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-600" />
              {reviewPO?.kind === 'receipt' ? 'Review Receive Stock' : 'Review Received PO'} — {reviewPO?.supplier}
            </DialogTitle>
            <DialogDescription>
              Spot-check what your staff accepted. Click any sell price to fix it — saved instantly to inventory.
              Rows below <span className="font-bold text-amber-700">{MIN_MARGIN_PERCENT}% margin</span> are highlighted yellow.
            </DialogDescription>
          </DialogHeader>

          {reviewPO && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-slate-50 rounded p-3">
                  <p className="text-xs uppercase text-slate-500">Received</p>
                  <p className="font-bold">{reviewPO.received_at ? formatDate(reviewPO.received_at) : '—'}</p>
                </div>
                <div className="bg-slate-50 rounded p-3">
                  <p className="text-xs uppercase text-slate-500">Items</p>
                  <p className="font-bold">{reviewPO.items?.length || 0}</p>
                </div>
                <div className="bg-emerald-50 rounded p-3">
                  <p className="text-xs uppercase text-emerald-700">Total</p>
                  <p className="font-bold text-emerald-700">${reviewPO.total_amount?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="bg-amber-50 rounded p-3">
                  <p className="text-xs uppercase text-amber-700">Low-margin lines</p>
                  <p className="font-bold text-amber-700">
                    {(reviewPO.items || []).filter(it => {
                      const prod = products.find(p => p.product_id === it.product_id);
                      const sell = prod?.sell_price || 0;
                      const cost = it.buy_price || 0;
                      if (!sell || !cost) return false;
                      // sell_price is inc-GST, buy_price ex-GST — strip GST for the real margin
                      const sellEx = sell / 1.15;
                      const margin = ((sellEx - cost) / sellEx) * 100;
                      return margin < MIN_MARGIN_PERCENT;
                    }).length}
                  </p>
                </div>
              </div>

              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="text-left p-2 px-3">Product</th>
                      <th className="text-right p-2">Qty</th>
                      <th className="text-right p-2">Cost</th>
                      <th className="text-right p-2 w-44">Sell Price (click to edit)</th>
                      <th className="text-right p-2">Margin %</th>
                      <th className="text-right p-2">Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reviewPO.items || []).map((it, idx) => {
                      const prod = products.find(p => p.product_id === it.product_id);
                      const sell = prod?.sell_price ?? 0;
                      const cost = it.buy_price || 0;
                      // sell_price is inc-GST, buy_price ex-GST — strip GST for the real margin
                      const sellEx = sell / 1.15;
                      const margin = sellEx > 0 ? ((sellEx - cost) / sellEx) * 100 : 0;
                      const isLow = sell > 0 && margin < MIN_MARGIN_PERCENT;
                      const isSaving = reviewSavingFor === it.product_id;
                      return (
                        <tr key={`${it.product_id}-${idx}`} className={`border-t ${isLow ? 'bg-amber-50' : ''}`} data-testid={`review-row-${it.product_id}`}>
                          <td className="p-2 px-3">
                            <div className="font-medium">{it.product_name || prod?.name}</div>
                            <div className="text-[10px] text-slate-400">{prod?.barcode || '—'}</div>
                          </td>
                          <td className="p-2 text-right">{it.quantity}</td>
                          <td className="p-2 text-right">${cost.toFixed(2)}</td>
                          <td className="p-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-slate-400">$</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={sell}
                                onChange={(e) => handleSellPriceLocalChange(it.product_id, e.target.value)}
                                onBlur={(e) => handleSellPriceCommit(it.product_id, e.target.value)}
                                className={`w-24 text-right h-8 ${isSaving ? 'opacity-60' : ''} ${isLow ? 'border-amber-400' : ''}`}
                                disabled={isSaving}
                                data-testid={`review-sell-${it.product_id}`}
                              />
                              {isSaving && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                            </div>
                          </td>
                          <td className="p-2 text-right">
                            {sell > 0 ? (
                              <Badge className={isLow ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-emerald-100 text-emerald-800 border border-emerald-300'}>
                                {margin.toFixed(1)}%
                              </Badge>
                            ) : <span className="text-slate-400 text-xs">—</span>}
                          </td>
                          <td className="p-2 text-right font-medium">${((it.quantity || 0) * cost).toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="text-xs text-slate-500">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1 text-amber-600" />
                Sell price changes save instantly to the product (used at POS immediately). No re-deploy needed.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewPO(null)} data-testid="close-review-po">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT PO DIALOG */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setInReceiveFlow(false); }}>
        <DialogContent className="max-w-[95vw] xl:max-w-[1400px] max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b">
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <Edit className="w-5 h-5" />
              {inReceiveFlow ? 'Step 2 of 2 — Review Lines & Balance' : 'Edit Purchase Order'}
              <Badge className="ml-auto bg-emerald-100 text-emerald-700 border border-emerald-300 normal-case text-xs font-medium">
                Auto-saved — safe to leave & return
              </Badge>
            </DialogTitle>
            <DialogDescription>
              {inReceiveFlow
                ? 'Adjust line prices & quantities until Lines + GST + Freight + CC balance to the supplier invoice total within ±$0.50.'
                : 'Adjust prices, quantities, or add notes for specials, free gifts, and out-of-stock items'}
            </DialogDescription>
          </DialogHeader>

          {/* Scrollable body — keeps the Accept & Update Inventory / Save Changes
              footer always reachable even with long supplier lists (Dolphin Clinic,
              NHT, etc.) */}
          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4" data-testid="edit-po-scroll-body">

          {editingPO && inReceiveFlow && (
            <div className="rounded-md border-2 border-sky-300 bg-sky-50 px-3 py-2 text-sm" data-testid="receive-invoice-banner">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <div><span className="text-[10px] uppercase text-slate-500 mr-1">Invoice #</span><span className="font-bold">{receiveSetup.invoice_number || '—'}</span></div>
                <div><span className="text-[10px] uppercase text-slate-500 mr-1">Due</span><span className="font-medium">{receiveSetup.due_date || '—'}</span></div>
                <div><span className="text-[10px] uppercase text-slate-500 mr-1">Total</span><span className="font-bold">${(parseFloat(receiveSetup.total_amount) || 0).toFixed(2)}</span></div>
                <div><span className="text-[10px] uppercase text-slate-500 mr-1">Status</span><span className={`font-bold ${receiveSetup.payment_status === 'paid' ? 'text-emerald-700' : 'text-amber-700'}`}>{receiveSetup.payment_status === 'paid' ? 'Paid' : 'Unpaid'}</span></div>
                <div><span className="text-[10px] uppercase text-slate-500 mr-1">Store</span><span className="font-medium">{storeName(receiveStore)}</span></div>
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-auto h-7 text-xs"
                  onClick={() => { setEditDialogOpen(false); setReceiveSetupOpen(true); }}
                  data-testid="receive-banner-edit-invoice"
                >
                  Edit Invoice Header
                </Button>
              </div>
            </div>
          )}

          {editingPO && inReceiveFlow && linesOverByGst() && (
            <div className="rounded-md border-2 border-amber-400 bg-amber-50 px-3 py-2 text-sm flex flex-wrap items-center gap-2" data-testid="gst-double-count-banner">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
              <span className="text-amber-900">
                You're over by <strong>exactly the GST</strong> — your line buy prices probably already <strong>include GST</strong>.
              </span>
              <Button
                size="sm"
                className="ml-auto h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                onClick={convertLinesToExGst}
                data-testid="convert-lines-ex-gst"
              >
                Convert lines to ex-GST (÷1.15)
              </Button>
            </div>
          )}

          {editingPO && (
            <div className="space-y-4">
              {/* Supplier Info */}
              <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                <Truck className="w-6 h-6 text-orange-600" />
                <div>
                  <p className="font-bold">{editingPO.supplier}</p>
                  <p className="text-sm text-slate-500">Created: {formatDate(editingPO.created_at)}</p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 items-start flex-wrap">
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setShowAddProductPopover(!showAddProductPopover); setCreateProductForm(null); }}
                    data-testid="edit-po-add-product-btn"
                  >
                    <Plus className="w-4 h-4 mr-1 text-emerald-600" /> Add Product
                  </Button>
                  {showAddProductPopover && !createProductForm && (
                    <div className="absolute z-50 mt-1 w-96 bg-white border-2 border-slate-200 rounded-lg shadow-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Search className="w-4 h-4 text-slate-400" />
                        <Input
                          autoFocus
                          value={addProductSearch}
                          onChange={(e) => setAddProductSearch(e.target.value)}
                          placeholder="Search product name or barcode…"
                          className="h-8 text-sm"
                          data-testid="edit-po-add-product-search"
                        />
                        <button onClick={() => setShowAddProductPopover(false)} className="text-slate-400 hover:text-slate-600 text-xs">✕</button>
                      </div>
                      <div className="max-h-64 overflow-y-auto -mx-1">
                        {addProductSearch.trim().length < 2 ? (
                          <div className="px-3 py-4 text-xs text-slate-400 text-center">Type at least 2 letters…</div>
                        ) : (() => {
                            const q = addProductSearch.trim().toLowerCase();
                            const matches = (products || [])
                              .filter(p => (p.name || '').toLowerCase().includes(q) || (p.barcode || '').includes(q))
                              .slice(0, 25);
                            return matches.length === 0 ? (
                              <div className="px-3 py-4 text-center space-y-2">
                                <p className="text-xs text-slate-500">No products match "{addProductSearch.trim()}"</p>
                                <Button
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                  onClick={openCreateProductForm}
                                  data-testid="edit-po-create-new-product-btn"
                                >
                                  <Plus className="w-3.5 h-3.5 mr-1" /> Create New Product
                                </Button>
                              </div>
                            ) : (
                              <>
                                {matches.map(p => (
                                  <button
                                    key={p.id}
                                    onClick={() => addProductToEditPO(p)}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-b last:border-b-0 flex justify-between items-center"
                                    data-testid={`edit-po-add-result-${p.id}`}
                                  >
                                    <span className="truncate font-medium">{p.name}</span>
                                    <span className="text-[10px] text-slate-400 ml-2 shrink-0">${(p.buy_price || 0).toFixed(2)}</span>
                                  </button>
                                ))}
                                {/* Always offer "Create new" too — supplier may have a product not in your DB yet */}
                                <button
                                  onClick={openCreateProductForm}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-t-2 border-emerald-200 text-emerald-700 font-semibold flex items-center"
                                  data-testid="edit-po-create-new-product-btn"
                                >
                                  <Plus className="w-3.5 h-3.5 mr-1" /> Create new product "{addProductSearch.trim()}"
                                </button>
                              </>
                            );
                          })()
                        }
                      </div>
                    </div>
                  )}
                  {/* Inline create-product mini-form */}
                  {showAddProductPopover && createProductForm && (
                    <div className="absolute z-50 mt-1 w-96 bg-white border-2 border-emerald-300 rounded-lg shadow-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-sm text-emerald-700">New Product</p>
                        <button onClick={() => setCreateProductForm(null)} className="text-slate-400 hover:text-slate-600 text-xs">✕</button>
                      </div>
                      <div>
                        <label className="text-[11px] text-slate-500 uppercase">Name *</label>
                        <Input
                          autoFocus
                          value={createProductForm.name}
                          onChange={(e) => setCreateProductForm({ ...createProductForm, name: e.target.value })}
                          className="h-8 text-sm"
                          data-testid="edit-po-create-name"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[11px] text-slate-500 uppercase">Barcode</label>
                          <Input
                            value={createProductForm.barcode}
                            onChange={(e) => setCreateProductForm({ ...createProductForm, barcode: e.target.value })}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] text-slate-500 uppercase">Supplier Code</label>
                          <Input
                            value={createProductForm.supplier_code}
                            onChange={(e) => setCreateProductForm({ ...createProductForm, supplier_code: e.target.value })}
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[11px] text-slate-500 uppercase">Buy Price *</label>
                          <Input
                            type="number" step="0.01"
                            value={createProductForm.buy_price}
                            onChange={(e) => setCreateProductForm({ ...createProductForm, buy_price: e.target.value })}
                            className="h-8 text-sm"
                            data-testid="edit-po-create-buy"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] text-slate-500 uppercase">Sell Price *</label>
                          <Input
                            type="number" step="0.01"
                            value={createProductForm.sell_price}
                            onChange={(e) => setCreateProductForm({ ...createProductForm, sell_price: e.target.value })}
                            className="h-8 text-sm"
                            data-testid="edit-po-create-sell"
                          />
                        </div>
                      </div>
                      <Button
                        className="w-full bg-emerald-600 hover:bg-emerald-700 h-8 text-sm"
                        onClick={submitCreateProduct}
                        disabled={createProductForm.creating}
                        data-testid="edit-po-create-submit"
                      >
                        {createProductForm.creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                        Create & Add to PO
                      </Button>
                    </div>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={addFreeGift}>
                  <Gift className="w-4 h-4 mr-1 text-pink-500" /> Add Free Gift
                </Button>
              </div>

              {/* Items Table */}
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-heading font-bold uppercase text-xs min-w-[320px]">Product</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center w-24">Qty</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-right w-32">Buy Price</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-right w-32">Sell Price</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center w-24">Margin</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-right w-28">Total</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs w-40">Notes</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editItems.map((item, idx) => (
                    <TableRow key={idx} className={item.buy_price === 0 ? 'bg-pink-50' : ''}>
                      <TableCell>
                        {item.product_id?.startsWith('gift-') ? (
                          <Input
                            value={item.product_name}
                            onChange={(e) => updateEditItem(idx, 'product_name', e.target.value)}
                            placeholder="Enter gift item name"
                            className="max-w-[280px]"
                          />
                        ) : (
                          <div>
                            <p className="font-medium leading-snug whitespace-nowrap overflow-hidden text-ellipsis" title={item.product_name}>{item.product_name}</p>
                            {item.notes && <Badge variant="outline" className="mt-1 bg-yellow-50 text-yellow-700">{item.notes}</Badge>}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          value={item.quantity}
                          onChange={(e) => updateEditItem(idx, 'quantity', parseInt(e.target.value) || 0)}
                          className="w-20 text-center"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-slate-500">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.buy_price}
                            onChange={(e) => updateEditItem(idx, 'buy_price', parseFloat(e.target.value) || 0)}
                            className="w-24 text-right"
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.product_id?.startsWith('gift-') ? (
                          <span className="text-slate-400 text-sm">-</span>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-slate-500">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={(() => {
                                const prod = products.find(p => p.product_id === item.product_id);
                                return prod?.sell_price ?? 0;
                              })()}
                              onChange={(e) => handleSellPriceLocalChange(item.product_id, e.target.value)}
                              onBlur={(e) => handleSellPriceCommit(item.product_id, e.target.value)}
                              onFocus={(e) => e.target.select()}
                              className={`w-24 text-right ${savingSellFor === item.product_id ? 'opacity-60' : ''}`}
                              disabled={savingSellFor === item.product_id}
                              data-testid={`po-sell-price-input-${idx}`}
                            />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {(() => {
                          if (item.product_id?.startsWith('gift-') || item.buy_price === 0) return <span className="text-slate-400">-</span>;
                          const prod = products.find(p => p.product_id === item.product_id);
                          const sellPrice = prod?.sell_price || 0;
                          if (sellPrice <= 0) return <span className="text-slate-400">N/A</span>;
                          // sell_price inc-GST, buy_price ex-GST — strip GST before margin calc
                          const sellExGst = sellPrice / 1.15;
                          const margin = ((sellExGst - item.buy_price) / sellExGst) * 100;
                          const color = margin < 30 ? 'text-red-600 bg-red-50' : margin < 42 ? 'text-amber-600 bg-amber-50' : 'text-emerald-600 bg-emerald-50';
                          return (
                            <Badge variant="outline" className={`${color} font-bold`} data-testid={`po-margin-${idx}`}>
                              {margin.toFixed(0)}%
                            </Badge>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        ${(item.quantity * item.buy_price).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={item.notes || 'none'}
                          onValueChange={(value) => updateEditItem(idx, 'notes', value === 'none' ? '' : value)}
                        >
                          <SelectTrigger className="w-36">
                            <SelectValue placeholder="Add note..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No note</SelectItem>
                            <SelectItem value="SPECIAL">SPECIAL</SelectItem>
                            <SelectItem value="FREE GIFT">FREE GIFT</SelectItem>
                            <SelectItem value="OOS">OOS (Out of Stock)</SelectItem>
                            <SelectItem value="DISCOUNT">DISCOUNT</SelectItem>
                            <SelectItem value="DAMAGED">DAMAGED</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-red-500 hover:bg-red-50"
                          onClick={() => removeEditItem(idx)}
                        >
                          <Ban className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2 p-4 bg-slate-50 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal (Ex GST)</span>
                    <span className="font-medium">${editSubtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>GST (15%)</span>
                    <span className="font-medium">${editGST.toFixed(2)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span>${editTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Order Notes</label>
                <Textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Add notes about this order (specials received, items missing, etc.)"
                  rows={3}
                />
              </div>

              {/* Receive Store Selection — hidden in receive flow (store already chosen in Step 1) */}
              {!inReceiveFlow && (
              <div className="p-4 bg-emerald-50 rounded-lg space-y-3">
                <p className="font-medium text-emerald-800">Receive stock to which store?</p>
                <div className="flex gap-3">
                  <Button
                    variant={receiveStore === 'orewa' ? 'default' : 'outline'}
                    onClick={() => setPOStore('orewa')}
                    className={receiveStore === 'orewa' ? 'bg-emerald-600' : ''}
                    data-testid="po-store-orewa"
                  >
                    {storeName('orewa')}
                  </Button>
                  <Button
                    variant={receiveStore === 'eastridge' ? 'default' : 'outline'}
                    onClick={() => setPOStore('eastridge')}
                    className={receiveStore === 'eastridge' ? 'bg-emerald-600' : ''}
                    data-testid="po-store-eastridge"
                  >
                    {storeName('eastridge')}
                  </Button>
                </div>
              </div>
              )}
            </div>
          )}

          </div>
          {/* /Scrollable body */}

          <DialogFooter className="flex gap-2 sm:justify-between shrink-0 border-t bg-white px-6 py-3">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <div className="flex gap-2 items-center">
              {inReceiveFlow && (() => {
                const subtotalF = (editItems || []).reduce((s, it) => s + (it.quantity * it.buy_price), 0);
                const gstF = parseFloat(receiveSetup.gst_amount) || 0;
                const freightF = parseFloat(receiveSetup.freight) || 0;
                const ccF = parseFloat(receiveSetup.cc_surcharge) || 0;
                const invoiceTotalF = parseFloat(receiveSetup.total_amount) || 0;
                const runningF = subtotalF + gstF + freightF + ccF;
                const remainingF = invoiceTotalF - runningF;
                const balancedF = Math.abs(remainingF) <= 0.50 && invoiceTotalF > 0 && editItems.length > 0;
                return (
                  <div className={`text-xs px-3 py-2 rounded border-2 ${balancedF ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : remainingF < -0.5 ? 'border-red-300 bg-red-50 text-red-800' : 'border-amber-300 bg-amber-50 text-amber-800'}`} data-testid="receive-balance-gauge">
                    <span className="font-bold">${runningF.toFixed(2)}</span>
                    <span className="text-slate-500 mx-1">/</span>
                    <span className="font-bold">${invoiceTotalF.toFixed(2)}</span>
                    <span className="ml-2 font-semibold">
                      {balancedF ? '✓ Balanced' : remainingF > 0.5 ? `Add $${remainingF.toFixed(2)}` : `Over $${Math.abs(remainingF).toFixed(2)}`}
                    </span>
                  </div>
                );
              })()}
              <Button variant="outline" onClick={handleSaveEdit}>
                <Edit className="w-4 h-4 mr-1" /> Save Changes
              </Button>
              {inReceiveFlow ? (() => {
                const subtotalF = (editItems || []).reduce((s, it) => s + (it.quantity * it.buy_price), 0);
                const gstF = parseFloat(receiveSetup.gst_amount) || 0;
                const freightF = parseFloat(receiveSetup.freight) || 0;
                const ccF = parseFloat(receiveSetup.cc_surcharge) || 0;
                const invoiceTotalF = parseFloat(receiveSetup.total_amount) || 0;
                const remainingF = invoiceTotalF - (subtotalF + gstF + freightF + ccF);
                const balancedF = Math.abs(remainingF) <= 0.50 && invoiceTotalF > 0 && editItems.length > 0;
                return (
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleReceivePO}
                    disabled={!balancedF || receiving || editItems.length === 0}
                    data-testid="accept-and-update-inventory-btn"
                  >
                    {receiving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Accept & Update Inventory
                  </Button>
                );
              })() : (
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => { setEditDialogOpen(false); handleStartReceive(editingPO); }}
                  disabled={receiving || editItems.length === 0}
                  data-testid="open-receive-setup-from-edit"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Receive Stock
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PRE-RECEIVE CUSTOMER ORDER WARNING — fuzzy-matches PO lines against pending customer orders */}
      <Dialog open={coWarningOpen} onOpenChange={setCoWarningOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-6 h-6" />
              Customer Orders Found In This PO
            </DialogTitle>
            <DialogDescription>
              {coWarningMatches.length} pending customer order{coWarningMatches.length === 1 ? '' : 's'} fuzzy-matched against the items in this purchase order.
              Please put these aside while receiving so they aren't missed, then call the customer{coWarningMatches.length === 1 ? '' : 's'} to let them know their stock has arrived.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[55vh] overflow-y-auto -mx-2 px-2">
            <div className="space-y-2" data-testid="co-warning-list">
              {coWarningMatches.map((co, idx) => (
                <div key={co.id || idx} className="border-2 border-amber-200 bg-amber-50 rounded-md p-3" data-testid={`co-warning-row-${idx}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-900">{co.customer_name || 'Unknown customer'}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-600 mt-0.5">
                        {co.phone && (
                          <a href={`tel:${co.phone}`} className="text-blue-600 hover:underline font-medium" data-testid={`co-warning-call-${idx}`}>
                            📞 {co.phone}
                          </a>
                        )}
                        {co.store && (
                          <Badge variant="outline" className={co.store === 'eastridge' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}>
                            {storeName(co.store)}
                          </Badge>
                        )}
                        {co.status && (
                          <Badge variant="outline" className="text-[10px]">{co.status.replace(/_/g, ' ')}</Badge>
                        )}
                      </div>
                      <div className="mt-1.5 text-sm">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1">Wants:</span>
                        <span className="text-slate-700">{co.items || '—'}</span>
                      </div>
                      {co.matched_products?.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {co.matched_products.map((mp, mi) => (
                            <span key={mi} className="inline-flex items-center text-[11px] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded font-medium" title="Matched against this PO line">
                              ✓ {mp}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="flex gap-2 sm:justify-between">
            <Button
              variant="outline"
              onClick={() => { setCoWarningOpen(false); setCoWarningMatches([]); setCoWarningPO(null); }}
              data-testid="co-warning-cancel"
            >
              Cancel — go back
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => {
                const po = coWarningPO;
                setCoWarningOpen(false);
                setCoWarningMatches([]);
                setCoWarningPO(null);
                if (po) _openReceiveSetupForPO(po);
              }}
              data-testid="co-warning-continue"
            >
              <Check className="w-4 h-4 mr-2" />
              Got it — start receiving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RECEIVE SETUP DIALOG — gates PO acceptance behind invoice + balance check */}
      <Dialog open={receiveSetupOpen} onOpenChange={setReceiveSetupOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">Step 1 of 2 — Supplier Invoice Setup</DialogTitle>
            <DialogDescription>
              Capture the supplier's invoice header first (invoice #, due date, total). In the next step you'll review line prices &amp; quantities and balance them to the invoice total within ±$0.50.
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const subtotal = (editItems || []).reduce((s, it) => s + (it.quantity * it.buy_price), 0);
            const gst = parseFloat(receiveSetup.gst_amount) || 0;
            const freight = parseFloat(receiveSetup.freight) || 0;
            const cc = parseFloat(receiveSetup.cc_surcharge) || 0;
            const invoiceTotal = parseFloat(receiveSetup.total_amount) || 0;
            const running = subtotal + gst + freight + cc;
            const remaining = invoiceTotal - running;
            const balanced = Math.abs(remaining) <= 0.50 && invoiceTotal > 0 && editItems.length > 0;

            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Supplier</label>
                    <Input value={editingPO?.supplier_name || ''} disabled className="bg-slate-50" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Store</label>
                    <select
                      value={receiveStore}
                      onChange={(e) => setPOStore(e.target.value)}
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm h-10"
                      data-testid="rs-po-store"
                    >
                      <option value="orewa">{storeName('orewa')}</option>
                      <option value="eastridge">{storeName('eastridge')}</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Invoice Number</label>
                    <Input
                      value={receiveSetup.invoice_number}
                      onChange={(e) => setReceiveSetup({ ...receiveSetup, invoice_number: e.target.value })}
                      placeholder="e.g. INV-2026-1234"
                      data-testid="rs-po-invoice-number"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Received Date</label>
                    <Input
                      type="date"
                      value={receiveSetup.received_date}
                      onChange={(e) => setReceiveSetup({ ...receiveSetup, received_date: e.target.value })}
                    />
                  </div>
                </div>

                {/* Due Date + Paid/Unpaid → these go to the Invoices page */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Due Date</label>
                    <Input
                      type="date"
                      value={receiveSetup.due_date}
                      onChange={(e) => setReceiveSetup({ ...receiveSetup, due_date: e.target.value })}
                      data-testid="rs-po-due-date"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Payment Status</label>
                    <div className="flex border border-slate-300 rounded overflow-hidden h-10">
                      <button
                        type="button"
                        onClick={() => setReceiveSetup({ ...receiveSetup, payment_status: 'unpaid' })}
                        className={`flex-1 text-sm font-bold ${receiveSetup.payment_status === 'unpaid' ? 'bg-amber-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                        data-testid="rs-po-status-unpaid"
                      >Unpaid</button>
                      <button
                        type="button"
                        onClick={() => setReceiveSetup({ ...receiveSetup, payment_status: 'paid' })}
                        className={`flex-1 text-sm font-bold border-l border-slate-300 ${receiveSetup.payment_status === 'paid' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                        data-testid="rs-po-status-paid"
                      >Paid</button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 uppercase">GST Amount *</label>
                    <Input
                      type="number" step="0.01"
                      value={receiveSetup.gst_amount}
                      onChange={(e) => setReceiveSetup({ ...receiveSetup, gst_amount: e.target.value })}
                      data-testid="rs-po-gst"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Total (GST inclusive) *</label>
                    <Input
                      type="number" step="0.01"
                      value={receiveSetup.total_amount}
                      onChange={(e) => setReceiveSetup({ ...receiveSetup, total_amount: e.target.value })}
                      data-testid="rs-po-total"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Freight</label>
                    <Input
                      type="number" step="0.01"
                      value={receiveSetup.freight}
                      onChange={(e) => setReceiveSetup({ ...receiveSetup, freight: e.target.value })}
                      placeholder="0.00"
                      data-testid="rs-po-freight"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase">CC Surcharge</label>
                    <Input
                      type="number" step="0.01"
                      value={receiveSetup.cc_surcharge}
                      onChange={(e) => setReceiveSetup({ ...receiveSetup, cc_surcharge: e.target.value })}
                      placeholder="0.00"
                      data-testid="rs-po-cc"
                    />
                  </div>
                </div>

                {/* Live running total — informational only at this step; final gate is on Step 2 */}
                <div className={`rounded-md border-2 p-3 ${balanced ? 'border-emerald-300 bg-emerald-50' : remaining < -0.5 ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
                  <div className="flex flex-wrap items-end gap-x-4 gap-y-1 text-sm">
                    <div><p className="text-[10px] uppercase text-slate-500">Lines (so far)</p><p className="font-bold">${subtotal.toFixed(2)}</p></div>
                    <span className="text-slate-400 pb-1">+</span>
                    <div><p className="text-[10px] uppercase text-slate-500">GST</p><p>${gst.toFixed(2)}</p></div>
                    {freight > 0 && (<><span className="text-slate-400 pb-1">+</span><div><p className="text-[10px] uppercase text-slate-500">Freight</p><p>${freight.toFixed(2)}</p></div></>)}
                    {cc > 0 && (<><span className="text-slate-400 pb-1">+</span><div><p className="text-[10px] uppercase text-slate-500">CC</p><p>${cc.toFixed(2)}</p></div></>)}
                    <span className="text-slate-400 pb-1">=</span>
                    <div><p className="text-[10px] uppercase text-slate-500">Running</p><p className="font-bold text-base">${running.toFixed(2)}</p></div>
                    <div className="ml-auto text-right"><p className="text-[10px] uppercase text-slate-500">Invoice</p><p className="font-bold text-base">${invoiceTotal.toFixed(2)}</p></div>
                  </div>
                  <div className="mt-2 pt-2 border-t text-xs text-slate-600">
                    You'll be able to adjust line prices &amp; quantities in the next step to balance to the invoice total.
                  </div>
                  {gst > 0 && Math.abs((running - invoiceTotal) - gst) <= 0.50 && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-amber-900 bg-amber-100 border border-amber-300 rounded p-2" data-testid="gst-double-count-hint-step1">
                      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Over by <strong>exactly the GST</strong> — your line buy prices probably already include GST.</span>
                      <Button size="sm" className="ml-auto h-6 text-xs bg-amber-600 hover:bg-amber-700 text-white" onClick={convertLinesToExGst} data-testid="convert-lines-ex-gst-step1">
                        Convert lines to ex-GST (÷1.15)
                      </Button>
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => { setReceiveSetupOpen(false); setInReceiveFlow(false); }}
                    data-testid="rs-po-setup-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={async () => {
                      const invNum = (receiveSetup.invoice_number || '').trim();
                      if (!invNum) return toast.error('Enter the supplier invoice number');
                      if (!(invoiceTotal > 0)) return toast.error('Enter the total invoice amount');
                      if (!((parseFloat(receiveSetup.gst_amount) || 0) >= 0)) return toast.error('Enter the GST amount');
                      // Duplicate guard — has this invoice number already been processed?
                      try {
                        const res = await axios.get(`${API_URL}/invoices/check-duplicate`, {
                          params: { invoice_number: invNum },
                          headers: getAuthHeader()
                        });
                        if (res.data?.exists) {
                          const dup = res.data.invoice || {};
                          const ok = window.confirm(
                            `⚠️ Invoice ${invNum} HAS ALREADY BEEN PROCESSED` +
                            `${dup.supplier_name ? ` (${dup.supplier_name}` : ''}${dup.received_date ? `, received ${dup.received_date}` : ''}${dup.supplier_name ? ')' : ''}.\n\n` +
                            `Accepting it again will ADD THE STOCK TWICE.\n\nAre you sure you want to accept this order?`
                          );
                          if (!ok) return;
                        }
                      } catch { /* never block receiving if the check itself fails */ }
                      setReceiveSetupOpen(false);
                      setEditDialogOpen(true);
                    }}
                    data-testid="rs-po-continue-to-lines"
                  >
                    Continue to Review Lines <Check className="w-4 h-4 ml-2" />
                  </Button>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
      <Dialog open={showLabelDialog} onOpenChange={setShowLabelDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <Tag className="w-5 h-5" />
              Print Product Labels
            </DialogTitle>
            <DialogDescription>
              Set how many labels to print for each product
            </DialogDescription>
          </DialogHeader>

          {/* Scrollable body — keeps the Print Labels button reachable when the
              product list is long (e.g. Dolphin Clinic / NHT bulk receives) */}
          <div className="flex-1 overflow-y-auto min-h-0 px-6 space-y-4" data-testid="print-labels-scroll-body">

            {/* Missing-barcode warning — caught real-world bug where Dolphin
                Clinic labels printed name+price only because the product DB
                row had no barcode. One-click fix to auto-generate. */}
            {(() => {
              const missingBarcodes = labelItems.filter(it => !((it.barcode || '').trim()));
              if (missingBarcodes.length === 0) return null;
              return (
                <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3" data-testid="missing-barcode-banner">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-900">
                        <p className="font-bold">
                          {missingBarcodes.length} product{missingBarcodes.length === 1 ? '' : 's'} {missingBarcodes.length === 1 ? 'has' : 'have'} no barcode
                        </p>
                        <p className="text-xs mt-0.5">
                          These labels will print as <strong>name + price only</strong> (no scannable barcode) — staff won't be able to scan them at the till. Click below to auto-generate internal barcodes for each.
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={generateMissingBarcodes}
                      disabled={generatingBarcodes}
                      className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                      data-testid="generate-missing-barcodes-btn"
                    >
                      {generatingBarcodes
                        ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Generating…</>
                        : <>Generate {missingBarcodes.length} barcode{missingBarcodes.length === 1 ? '' : 's'}</>}
                    </Button>
                  </div>
                </div>
              );
            })()}

            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-heading font-bold uppercase text-xs">Product</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs text-center w-32">Qty Received</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs text-center w-32">Labels to Print</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {labelItems.map((item) => {
                  const hasBarcode = !!((item.barcode || '').trim());
                  return (
                  <TableRow key={item.product_id}>
                    <TableCell>
                      <p className="font-medium">{item.product_name}</p>
                      {hasBarcode ? (
                        <p className="text-[10px] text-slate-400 font-mono mt-0.5">{item.barcode}</p>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 text-[10px] mt-0.5" data-testid={`no-barcode-${item.product_id}`}>
                          ⚠ no barcode — label will print without scan code
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center text-slate-500">
                      {item.quantity}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8"
                          onClick={() => updateLabelCount(item.product_id, (labelsPerProduct[item.product_id] || 0) - 1)}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <Input
                          type="number"
                          min="0"
                          value={labelsPerProduct[item.product_id] || 0}
                          onChange={(e) => updateLabelCount(item.product_id, parseInt(e.target.value) || 0)}
                          className="w-16 h-8 text-center"
                        />
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8"
                          onClick={() => updateLabelCount(item.product_id, (labelsPerProduct[item.product_id] || 0) + 1)}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Sticky summary + footer — always visible regardless of list length */}
          <div className="border-t bg-white px-6 py-3 shrink-0 space-y-3">
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <span className="font-medium">Total Labels:</span>
              <Badge className="text-lg px-3 py-1" data-testid="print-labels-total">
                {Object.values(labelsPerProduct).reduce((sum, count) => sum + count, 0)}
              </Badge>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLabelDialog(false)}>
                Cancel
              </Button>
              <Button onClick={printLabels} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-print-labels-btn">
                <Printer className="w-4 h-4 mr-2" />
                Print Labels
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* MARGIN WARNING DIALOG - NO OVERRIDE FOR STOCK RECEIVING */}
      <Dialog open={showMarginWarning} onOpenChange={setShowMarginWarning}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-6 h-6" />
              {lowMarginItems.length} product{lowMarginItems.length === 1 ? '' : 's'} in this order {lowMarginItems.length === 1 ? 'is' : 'are'} below the {MIN_MARGIN_PERCENT}% margin
            </DialogTitle>
            <DialogDescription>
              {marginDialogMode === 'ask'
                ? 'Would you like to increase prices? This will not stop you from accepting the order.'
                : 'Change the prices you want — leave the rest blank. The order is accepted either way.'}
            </DialogDescription>
          </DialogHeader>

          {marginDialogMode === 'ask' ? (
            <>
              <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="low-margin-ask-list">
                {lowMarginItems.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm p-2 rounded bg-amber-50 border border-amber-100">
                    <span className="font-medium truncate mr-2">{item.product_name}</span>
                    <span className="text-amber-700 font-semibold shrink-0">{item.margin}%</span>
                  </div>
                ))}
              </div>
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  className="flex-1 h-12 text-base"
                  disabled={receiving}
                  onClick={() => { setShowMarginWarning(false); confirmReceiveWithInvoice(); }}
                  data-testid="low-margin-no-accept"
                >
                  No — Accept Order As-Is
                </Button>
                <Button
                  className="flex-1 h-12 text-base bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => setMarginDialogMode('fix')}
                  data-testid="low-margin-yes-review"
                >
                  Yes — Increase Prices
                </Button>
              </DialogFooter>
            </>
          ) : (
          <>
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow className="bg-red-50">
                  <TableHead className="font-heading font-bold uppercase text-xs">Product</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs text-right">Buy</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs text-right">Min Sell</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs text-right w-40">New Sell Price</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs text-center">New Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lowMarginItems.map((item, idx) => {
                  const newSell = parseFloat(item.new_sell_price) || 0;
                  // Real margin: strip GST from the inc-GST sell price first
                  const newSellEx = newSell / 1.15;
                  const newMargin = newSellEx > 0 ? ((newSellEx - item.buy_price) / newSellEx) * 100 : 0;
                  const ok = newMargin >= MIN_MARGIN_PERCENT - 0.001;
                  return (
                    <TableRow key={idx} className="border-b border-red-100">
                      <TableCell className="font-medium" data-testid={`low-margin-row-name-${idx}`}>
                        <p>{item.product_name}</p>
                        <p className="text-xs text-slate-400">Current: ${item.sell_price.toFixed(2)} @ {item.margin}%</p>
                      </TableCell>
                      <TableCell className="text-right">${item.buy_price.toFixed(2)}</TableCell>
                      <TableCell className="text-right text-emerald-700 font-semibold">${item.required_sell.toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-slate-500 font-semibold">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.new_sell_price ?? ''}
                            placeholder={item.required_sell.toFixed(2)}
                            onChange={(e) => {
                              const v = e.target.value;
                              setLowMarginItems(prev => prev.map((it, i) => i === idx ? { ...it, new_sell_price: v } : it));
                            }}
                            onFocus={(e) => e.target.select()}
                            className="w-32 h-10 text-right text-base font-semibold border-2 border-emerald-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 bg-white"
                            data-testid={`low-margin-new-sell-input-${idx}`}
                            autoFocus={idx === 0}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {newSell > 0 ? (
                          <Badge className={ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} data-testid={`low-margin-new-margin-${idx}`}>
                            {newMargin.toFixed(0)}%
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
              Only fill the lines you want to change — <strong>Auto-fill Min Sell</strong> sets all of them, or type your own. Blank lines keep their current price. Price changes save to Inventory immediately.
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMarginDialogMode('ask')} data-testid="low-margin-cancel">
              Back
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setLowMarginItems(prev => prev.map(it => ({ ...it, new_sell_price: it.required_sell.toFixed(2) })));
              }}
              data-testid="low-margin-autofill"
            >
              Auto-fill Min Sell
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={savingMarginFix}
              onClick={handleSaveMarginFixAndReceive}
              data-testid="low-margin-save-receive"
            >
              {savingMarginFix ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : <><CheckCircle className="w-4 h-4 mr-1" /> Save Prices & Accept Order</>}
            </Button>
          </DialogFooter>
          </>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Receive Dialog */}
      <Dialog open={quickReceiveDialogOpen} onOpenChange={setQuickReceiveDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2 text-emerald-600">
              <CheckCircle className="w-6 h-6" />
              Receive Stock
            </DialogTitle>
            <DialogDescription>
              {quickReceivePO && (
                <span>
                  Receiving <strong>{quickReceivePO.items?.length || 0} items</strong> from <strong>{quickReceivePO.supplier}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="font-medium text-slate-700 mb-3">Select store to receive stock:</p>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant={quickReceiveStore === 'orewa' ? 'default' : 'outline'}
                className={quickReceiveStore === 'orewa' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                onClick={() => setQuickReceiveStore('orewa')}
                data-testid="quick-receive-orewa"
              >
                <Store className="w-4 h-4 mr-2" />
                {storeName('orewa')}
              </Button>
              <Button
                variant={quickReceiveStore === 'eastridge' ? 'default' : 'outline'}
                className={quickReceiveStore === 'eastridge' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                onClick={() => setQuickReceiveStore('eastridge')}
                data-testid="quick-receive-eastridge"
              >
                <Store className="w-4 h-4 mr-2" />
                {storeName('eastridge')}
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickReceiveDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={confirmQuickReceive}
              disabled={receiving}
              data-testid="confirm-quick-receive"
            >
              {receiving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Receiving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Receive Stock
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Order Dialog */}
      <Dialog open={importOrderOpen} onOpenChange={(open) => { setImportOrderOpen(open); if (!open) { setImportOrderFile(null); setImportOrderResult(null); setImportOrderText(''); setImportOrderMode('file'); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-500" /> Import Online Order
            </DialogTitle>
            <DialogDescription>
              Drop a supplier invoice/screenshot, or paste an order confirmation email. AI will extract products and create a pending PO for staff to review.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {!importOrderResult ? (
              <>
                <Tabs value={importOrderMode} onValueChange={setImportOrderMode}>
                  <TabsList className="grid grid-cols-2 w-full">
                    <TabsTrigger value="file" data-testid="import-mode-file">Upload File</TabsTrigger>
                    <TabsTrigger value="email" data-testid="import-mode-email">Paste Email</TabsTrigger>
                  </TabsList>

                  <TabsContent value="file" className="mt-4">
                    <div 
                      className="border-2 border-dashed border-amber-300 rounded-lg p-8 text-center cursor-pointer hover:border-amber-500 hover:bg-amber-50/50 transition-colors"
                      onClick={() => importOrderRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setImportOrderFile(f); }}
                      data-testid="import-order-dropzone"
                    >
                      <Upload className="w-10 h-10 mx-auto text-amber-400 mb-3" />
                      <p className="text-sm font-medium text-slate-700">
                        {importOrderFile ? importOrderFile.name : 'Drop your order file here or click to browse'}
                      </p>
                      <p className="text-xs text-slate-500 mt-2">
                        Supports: PDF, CSV, Excel, Screenshots (JPG/PNG)
                      </p>
                      <input
                        ref={importOrderRef}
                        type="file"
                        accept=".csv,.xlsx,.xls,.pdf,.jpg,.jpeg,.png,.webp"
                        className="hidden"
                        onChange={(e) => { if (e.target.files[0]) setImportOrderFile(e.target.files[0]); }}
                        data-testid="import-order-file-input"
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="email" className="mt-4">
                    <div className="space-y-2">
                      <p className="text-xs text-slate-500">
                        Paste the supplier's order confirmation email (subject, body, and product table). AI will pick out each product, quantity, and price.
                      </p>
                      <Textarea
                        value={importOrderText}
                        onChange={(e) => setImportOrderText(e.target.value)}
                        placeholder="Paste the entire email here, including the product list..."
                        rows={10}
                        className="font-mono text-xs"
                        data-testid="import-order-email-textarea"
                      />
                      <p className="text-[11px] text-slate-400">{importOrderText.length} characters</p>
                    </div>
                  </TabsContent>
                </Tabs>

                {((importOrderMode === 'file' && importOrderFile) || (importOrderMode === 'email' && importOrderText.trim().length >= 20)) && (
                  <Button 
                    onClick={handleImportOrder} 
                    disabled={importOrderLoading}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="import-order-submit"
                  >
                    {importOrderLoading 
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing order...</>
                      : <><Upload className="w-4 h-4 mr-2" /> Import & Create PO</>
                    }
                  </Button>
                )}
              </>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="font-bold text-green-800">PO Created Successfully</p>
                  </div>
                  <div className="space-y-1 text-sm text-green-700">
                    <p>PO Number: <span className="font-bold">{importOrderResult.po_number}</span></p>
                    <p>Supplier: <span className="font-bold">{importOrderResult.supplier}</span></p>
                    <p>Items: <span className="font-bold">{importOrderResult.items_count}</span></p>
                    <p>Total: <span className="font-bold">${importOrderResult.total_amount?.toFixed(2)}</span></p>
                  </div>
                </div>
                
                {importOrderResult.new_products_created > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-blue-800">
                      {importOrderResult.new_products_created} new product(s) added to inventory:
                    </p>
                    <ul className="mt-1 text-xs text-blue-700 list-disc pl-4">
                      {importOrderResult.new_product_names?.map((name, i) => (
                        <li key={i}>{name}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <p className="text-sm text-slate-500">
                  The PO is now in the <strong>Pending</strong> tab. Staff can review prices and quantities, then mark as received to update stock.
                </p>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => { setImportOrderFile(null); setImportOrderResult(null); setImportOrderText(''); }}
                    className="flex-1"
                  >
                    Import Another
                  </Button>
                  <Button 
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                    onClick={() => { setImportOrderOpen(false); setActiveTab('pending'); }}
                  >
                    Go to Pending POs
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Invoice */}
      <CustomerInvoiceDialog
        open={customerInvoiceOpen}
        onOpenChange={setCustomerInvoiceOpen}
        getAuthHeader={getAuthHeader}
        stores={stores}
        businessName={user?.business_name || 'SupplyTrack'}
      />

    </div>
  );
};

export default SmartPO;
